import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Action, createSelector, NgxsOnInit, State, StateContext, Store } from '@ngxs/store';
import { patch, updateItem } from '@ngxs/store/operators';
import { AuthenticationService, NotificationService } from '@orion-platform/core';
import { Helper } from 'apps/orion/src/app/helper/helper';
import * as FileSaver from 'file-saver';
import * as objectHash from 'object-hash';
import { firstValueFrom, from, Observable, of, Subscription, tap } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap, take } from 'rxjs/operators';
import {
  AddCommit,
  AddCommitError,
  AddCommitNotify,
  AddCommitSuccess,
  AddDummyCommit,
  AddDummyCommitError,
  AddDummyCommitHeader,
  AddDummyCommitHeaderError,
  AddDummyCommitHeaderSuccess,
  AddDummyCommitSuccess,
  AddUserSettings,
  AddUserSettingsError,
  AddUserSettingsSuccess,
  CacheCommitsCarriers,
  CacheCommitsCarriersError,
  CacheCommitsCarriersSuccess,
  CacheCommitsCountries,
  CacheCommitsCountriesError,
  CacheCommitsCountriesSuccess,
  CacheCommitsReasons,
  CacheCommitsReasonsError,
  CacheCommitsReasonsSuccess,
  CacheCommitsTransportType,
  CacheCommitsTransportTypeError,
  CacheCommitsTransportTypeSuccess,
  CacheCommitsWithDummy,
  CacheCommitsWithDummyError,
  CacheCommitsWithDummySuccess,
  CacheForecast,
  CacheForecastError,
  CacheForecastSuccess,
  CacheReadOnlyFields,
  CacheReadOnlyFieldsError,
  CacheReadOnlyFieldsSuccess,
  CacheVendorsName,
  CacheVendorsNameError,
  CacheVendorsNameSuccess,
  CancelCommit,
  CancelCommitError,
  CancelCommitSuccess,
  CreateSVComment,
  CreateSVCommentError,
  CreateSVCommentSuccess,
  CreateSVNote,
  CreateSVNoteSuccess,
  CreateSVPredefinedComment,
  CreateTemplateSettings,
  CreateTemplateSettingsError,
  CreateTemplateSettingsSuccess,
  DeleteCommit,
  DeleteCommitError,
  DeleteCommitSuccess,
  DeleteDummyCommit,
  DeleteDummyCommitError,
  DeleteDummyCommitHeader,
  DeleteDummyCommitHeaderError,
  DeleteDummyCommitHeaderSuccess,
  DeleteDummyCommitSuccess,
  DeletePLO,
  DeletePLOError,
  DeletePLOSuccess,
  DeletePnFlags,
  DeletePnFlagsError,
  DeletePnFlagsSuccess,
  DeleteSVComment,
  DeleteSVCommentError,
  DeleteSVCommentSuccess,
  DeleteSVNote,
  DeleteSVPredefinedComment,
  DeleteTemplateSettings,
  DeleteTemplateSettingsError,
  DeleteTemplateSettingsSuccess,
  DeleteUserSettings,
  DownloadNotificationFile,
  EditCommit,
  EditCommitError,
  EditCommitSuccess,
  EditDummyCommit,
  EditDummyCommitError,
  EditDummyCommitSuccess,
  EditUserSettings,
  EditUserSettingsError,
  EditUserSettingsSuccess,
  GetDummyCommitHeaders,
  GetDummyCommitHeadersError,
  GetDummyCommitHeadersSuccess,
  GetFilterAdditions,
  GetFilterAdditionsError,
  GetFilterAdditionsSuccess,
  GetMultiProjections,
  GetMultiProjectionsError,
  GetMultiProjectionsSuccess,
  GetNotificationDetails,
  GetNotificationDetailsError,
  GetNotificationDetailsSuccess,
  GetNotifications,
  GetNotificationsError,
  GetNotificationsSuccess,
  GetNotificationsUnreadCount,
  GetNotificationsUnreadCountSuccess,
  GetPNsPrecalculateProjectionByFilter,
  GetTemplateSettings,
  GetTemplateSettingsByID,
  GetTemplateSettingsByIDError,
  GetTemplateSettingsByIDSuccess,
  GetTemplateSettingsError,
  GetTemplateSettingsSuccess,
  HandleLinkEvents,
  HandleLinkEventsError,
  HandleLinkEventsSuccess,
  PatchCommitTransactionDetails,
  PatchVendorTransactionDetails,
  ResetCache,
  ResetCommitTransactionDetails,
  ResetPlantDependentCache,
  ResetSupplyVisibilityAndCommits,
  ResetSupplyVisibilityPartNumberList,
  SetActiveMMView,
  SetActiveVendor,
  SetAdditionalFilters,
  SetAllNotificationsAsRead,
  SetBasicParameters,
  SetBuyersPartNumbers,
  SetChildrenForecastSuccess,
  SetChildrenPNList,
  SetCommitModuleOn,
  SetCommits,
  SetCommitsError,
  SetCommitStatus,
  SetCommitSuccess,
  SetCommitsWithDummy,
  SetCommitsWithDummyError,
  SetCommitsWithDummySuccess,
  SetCommitsWithFilter,
  SetCommitsWithFilterError,
  SetCommitsWithFilterSuccess,
  SetCommitTransactionDetails,
  SetCommitVendorCode,
  SetCommitVendorCodeSuccess,
  SetDateRangeParameters,
  SetDateRangeParametersSuccess,
  SetDefaultDashboardSettings,
  SetDummyCommitHeader,
  SetForecast,
  SetForecastError,
  SetForecastSuccess,
  SetForecastWeekRange,
  SetMaterialManagementViews,
  SetPanelCacheSuccess,
  SetPartNumberListParameters,
  SetPartNumberListParametersSuccess,
  SetPlantMandatoryDates,
  SetPlantMandatoryDatesError,
  SetPlantMandatoryDatesSuccess,
  SetPnReviewed,
  SetPnReviewedError,
  SetPnReviewedSuccess,
  SetPurchaseOrders,
  SetPurchaseOrdersForCM,
  SetPurchaseOrdersSuccess,
  SetSVComments,
  SetSVCommentsError,
  SetSVCommentsSuccess,
  SetSVNotesByRecord,
  SetSVNotesByRecordError,
  SetSVNotesByRecordSuccess,
  SetSVPredefinedComments,
  SetUserAction,
  SetUserSettingParameters,
  SetUserSettings,
  SetUserSettingsError,
  SetUserSettingsSuccess,
  SetVendorCodes,
  SetVendorCodesError,
  SetVendorCodesSuccess,
  SetVendorTransactionDetails,
  ShowNotifications,
  TriggerLinkEventCommitDialog,
  UpdateDummyCommitHeader,
  UpdateDummyCommitHeaderError,
  UpdateDummyCommitHeaderSuccess,
  UpdateSVComment,
  UpdateSVCommentError,
  UpdateSVCommentSuccess,
  UpdateSVNote,
  UpdateSVPredefinedComment,
  UpdateTemplateSettings,
  UpdateTemplateSettingsError,
  UpdateTemplateSettingsSuccess,
} from './supply-visibility.actions';

import { inject, Injectable } from '@angular/core';
import { DateService } from 'apps/orion/src/app/services/date.service';
import {
  Carriers,
  CommitTransactionDetails,
  Countries,
  EnumCommitType,
  Reason,
  VendorName,
} from '../../models/commit.model';
import { Forecast, ForecastVirtualVc } from '../../models/forecast.model';
import {
  BuyersPartnumbers,
  BuyersPartnumbersResult,
} from '../../models/mm-buyers-partnumbers.model';
import { MaterialManagementViews } from '../../models/new-mm-view.model';
import {
  ApiResponse,
  BasicParameters,
  Commit,
  CumulativePayload,
  DateRangeParameters,
  DummyCommitDetail,
  DummyCommitHeader,
  EnumSvSidebarSection,
  NewCommit,
  NewUserSetting as NewUserSettings,
  PnReviewed,
  PurchaseOrders,
  UserSettings,
} from '../../models/supply-visibility.model';
import { SVNotes, SVNotesResult } from '../../models/sv-notes.model';
import { SVNotification } from '../../models/sv-notifications.model';
import { SVPredefinedComments } from '../../models/sv-predefined-comments.model';
import { PartNumberVendorCodes, VendorCode } from '../../models/vendor-codes-rights.model';
import { CommitsService } from '../../service/commits.service';
import { DefaultFormFieldsRegistry } from '../../service/default-form-fields.registry';
import { SupplyVisibilityCommentService } from '../../service/supply-visibility-comment.service';
import { SupplyVisibilityNotesService } from '../../service/supply-visibility-notes.service';
import { SupplyVisibilityPredefinedCommentService } from '../../service/supply-visibility-predefined-comment.service';
import { SupplyVisibilityService } from '../../service/supply-visibility.service';

import { EnumVendorType, VendorTransactionDetails } from '../vendors-list/vendors-list.types';
import { SVComments } from './../../models/sv-comments.model';
import { BuyersPartnumbersListService } from './../../service/buyers-partnumbers-list.service';
import { defaultSupplyVisibilityState } from './supply-visibility.consts';
import {
  DummyCommitReturnHeader,
  SupplyVisibilityStateModel,
  SVActiveFetch,
} from './supply-visibility.types';
import {
  buildVendorNameMap,
  normalizeVendorsList,
  transformItemsWithVendorNames,
  transformVendorCodesResponse,
} from './supply-visibility.utils';

@State<SupplyVisibilityStateModel>({
  defaults: defaultSupplyVisibilityState,
  name: 'supplyvisibility',
})
@Injectable()
export class SupplyVisibilityState implements NgxsOnInit {
  private activeFetchInfo: SVActiveFetch = {
    forecast: { currentPnId: '', nextPnId: '' },
    notificationDetailId: null,
  };
  private forecastSubs: Subscription = new Subscription();
  private commitsSub: Subscription = new Subscription();
  forecastsData: Record<string, unknown> = {};
  virtualPnChildList: unknown;
  static isForecastDataChanged = false;

  private commitsService = inject(CommitsService);
  private supplyVisibilityService = inject(SupplyVisibilityService);
  private supplyVisibilityCommentService = inject(SupplyVisibilityCommentService);
  private supplyVisibilityPredefinedCommentService = inject(
    SupplyVisibilityPredefinedCommentService,
  );
  private supplyVisibilityNotesService = inject(SupplyVisibilityNotesService);
  private supplyVisibilityBuyersPartNumbersService = inject(BuyersPartnumbersListService);
  private dialog = inject(MatDialog);
  private store = inject(Store);
  private translate = inject(TranslateService);
  private notification = inject(NotificationService);
  private dateService = inject(DateService);
  private authService = inject(AuthenticationService);
  private defaultFormFields = inject(DefaultFormFieldsRegistry);

  constructor() {}

  ngxsOnInit(): void {
    this.initNotifications();
  }

  //dummy commit nemá všechny sloupce jako normální commit, doplnuji se konkrétní
  private normalizeCommitForHistoryGrid(commit: Commit): Commit {
    const commitWithModifiedDates = this.dateService.commitHistoryTableDates({ ...commit });

    if (commitWithModifiedDates.isModifyByDummy === true && commitWithModifiedDates.dummyCommit) {
      const dummyCommitSource = commitWithModifiedDates.dummyCommit;
      const dummyCommitForGrid: Commit = {
        ...dummyCommitSource,
      };

      dummyCommitForGrid.lastUpdateDate =
        dummyCommitForGrid.lastUpdateDate || commitWithModifiedDates.lastUpdateDate;
      dummyCommitForGrid.responsibleEmail =
        dummyCommitForGrid.responsibleEmail || commitWithModifiedDates.responsibleEmail;
      dummyCommitForGrid.creator = dummyCommitForGrid.creator || commitWithModifiedDates.creator;
      dummyCommitForGrid.createDate =
        dummyCommitForGrid.createDate || commitWithModifiedDates.createDate;

      const normalizedDummyCommit = this.dateService.commitHistoryTableDates(dummyCommitForGrid);

      normalizedDummyCommit.inboundDeliveryNumber =
        '*' +
        (normalizedDummyCommit.inboundDeliveryNumber
          ? normalizedDummyCommit.inboundDeliveryNumber
          : '');

      normalizedDummyCommit.isModifyByDummy = true;

      return normalizedDummyCommit;
    }

    return commitWithModifiedDates;
  }

  // Get stored/default columns.

  // Columns and predefined settings were successfully loaded.

  /**
   * Get Commit
   * @static
   * @param {string} id
   * @returns
   * @memberof SupplyVisibilityState
   */
  static getCommit(id: string): (state: SupplyVisibilityStateModel) => Commit {
    return createSelector([SupplyVisibilityState], (state: SupplyVisibilityStateModel): Commit => {
      const filtredCommit = state.commits.filter(
        (data): boolean =>
          data.inboundDeliveryNumber !== null &&
          (data.inboundDeliveryNumber || '').toLocaleLowerCase().indexOf(id.toLocaleLowerCase()) !==
            -1,
      );
      return filtredCommit[0];
    });
  }

  /**
   * Selector for selected Purchase Order
   * @static
   * @param {string} selectedPuchaseOrderNumber
   * @returns
   * @memberof SupplyVisibilityState
   */
  static getPurchaseOrder(
    selectedPuchaseOrderNumber: string,
  ): (state: SupplyVisibilityStateModel) => string[] {
    return createSelector(
      [SupplyVisibilityState],
      (state: SupplyVisibilityStateModel): string[] => {
        const selectedPO = state.purchaseOrders.filter(
          (data: PurchaseOrders): boolean =>
            data.purchaseOrderNumber
              .toLocaleLowerCase()
              .indexOf(selectedPuchaseOrderNumber.toLocaleLowerCase()) !== -1,
        );
        if (selectedPO && selectedPO[0] && selectedPO[0].purchaseOrderItems) {
          return selectedPO[0].purchaseOrderItems.map(
            (POItem): string => POItem.purchaseOrder_Item,
          );
        }
        return [];
      },
    );
  }

  /**
   * Set Commits from API
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, patchState }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(SetCommits)
  setCommits({
    dispatch,
    getState,
    patchState,
  }: StateContext<SupplyVisibilityStateModel>): Subscription {
    const state = getState();
    const parameters: BasicParameters = state.basicParameters;
    const daterangeParameters = state.daterangeParameters;

    return this.supplyVisibilityService.getCommits(parameters, daterangeParameters).subscribe({
      error: (error: unknown): void => {
        dispatch(new SetCommitsError(error));
      },
      next: (response: Commit[]): void => {
        if (response) {
          patchState({ commits: [...response] });
          dispatch(new SetCommitSuccess());
        }
      },
    });
  }

  /**
   * Set Commit with Dummy Error
   * @memberof SupplyVisibilityState
   */
  @Action(CacheCommitsWithDummyError)
  cacheCommitsWithDummyError(): void {}

  /**
   * Set Commit with Dummy Success
   * @memberof SupplyVisibilityState
   */
  @Action(CacheCommitsWithDummySuccess)
  cacheCommitWithDummySuccess(): void {}

  /**
   * Set Commits with Dummy commits from API
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, patchState }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(CacheCommitsWithDummy)
  async cacheCommitsWithDummy(
    { dispatch, getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { partNumber }: CacheCommitsWithDummy,
  ): Promise<void> {
    const state = getState();
    const parameters: BasicParameters = {
      ...state.basicParameters,
      partNumber: partNumber.partNumber,
      vendorCode: partNumber.vendorCode,
    };
    const daterangeParameters = state.daterangeParameters;
    const dummyCommitHeader = state.selectedDummyCommitHeader;
    const commitStatus = state.commitStatus;

    // create hash for parameters
    const parametersForHash = [parameters, daterangeParameters, dummyCommitHeader];
    const parametersHash = objectHash.default(parametersForHash);
    console.log('parametersHash', parametersHash);

    this.supplyVisibilityService
      .getCommitsWithDummy(parameters, daterangeParameters, dummyCommitHeader, commitStatus)
      .subscribe({
        error: (error: unknown): void => {
          dispatch(new CacheCommitsWithDummyError(error));
        },
        next: (response: Commit[]): void => {
          const modifiedResponse = response.map((commit: Commit): Commit =>
            this.normalizeCommitForHistoryGrid(commit),
          );
          if (response) {
            if (!this.isValidPn(getState(), partNumber.partNumber, partNumber.vendorCode)) {
              return;
            }
            patchState({ cachedCommits: [...modifiedResponse], cachedCommitsHash: parametersHash });
            dispatch(new CacheCommitsWithDummySuccess());
          }
        },
      });
  }

  /**
   * Set Commits with Dummy commits from API
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, patchState }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(SetCommitsWithDummy)
  setCommitsWithDummy(
    { dispatch, getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { isRefreshCommit }: SetCommitsWithDummy,
  ): Subscription | void {
    const state = getState();
    if (state.commitModuleOn) {
      return; //do nothing as we are in commit module
    }
    const parameters: BasicParameters = state.basicParameters;
    const daterangeParameters = state.daterangeParameters;
    const dummyCommitHeader = state.selectedDummyCommitHeader;
    const commitStatus = state.commitStatus;

    // create hash for parameters
    const parametersForHash = [parameters, daterangeParameters, dummyCommitHeader];
    const parametersHash = objectHash.default(parametersForHash);
    console.log('parametersHash SetCommitsWithDummy', parametersHash);

    // check if we have cached commit
    const cachedCommits = state.cachedCommits;
    const cachedCommitsHash = state.cachedCommitsHash;
    console.log('cachedCommits', cachedCommits);

    if (
      isRefreshCommit == false &&
      cachedCommits !== null &&
      parametersHash === cachedCommitsHash
    ) {
      if (!this.isValidPn(getState(), parameters.partNumber, parameters.vendorCode as string)) {
        return;
      }
      console.log('Returning cached commits...');
      patchState({
        cachedCommits: undefined,
        cachedCommitsHash: '',
        commits: [...(cachedCommits as Commit[])],
      });
      // without timeout catchSetCommitsWithDummySuccess in commit-history.component.ts isn't catched
      setTimeout((): void => {
        dispatch(new SetCommitsWithDummySuccess());
      }, 100);
    } else {
      if (this.commitsSub) {
        this.commitsSub.unsubscribe();
      }
      console.log('Fetching commits...');
      this.commitsSub = this.supplyVisibilityService
        .getCommitsWithDummy(parameters, daterangeParameters, dummyCommitHeader, commitStatus)
        .pipe(debounceTime(500))
        .subscribe({
          error: (error: unknown): void => {
            dispatch(new SetCommitsWithDummyError(error));
          },
          next: (response: Commit[]): void => {
            if (response) {
              //Adding and updating commitTransaction data in  state (For setting index of commit)
              const result = this.modifyCommitTransaction(response, getState());

              if (result) {
                patchState({ commitTransactionDetails: result });
              }
            }

            const modifiedResponse = response.map((commit: Commit): Commit =>
              this.normalizeCommitForHistoryGrid(commit),
            );

            if (response) {
              if (
                !this.isValidPn(getState(), parameters.partNumber, parameters.vendorCode as string)
              ) {
                return;
              }
              patchState({ commits: [...modifiedResponse] });
              dispatch(new SetCommitsWithDummySuccess());
            }
          },
        });
      return this.commitsSub;
    }
  }

  /**
   * Set Commits with Dummy commits from API
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, patchState }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(SetCommitsWithFilter)
  async setCommitsWithFilter(
    { dispatch, getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { filter }: SetCommitsWithFilter,
  ): Promise<Subscription> {
    return this.commitsService.getCommitsForBatchEdit(filter).subscribe({
      error: (error: unknown): void => {
        patchState({
          commitFilter: null,
        });
        dispatch(new SetCommitsWithFilterError(error));
      },
      next: (response: Commit[]): void => {
        let modifiedResponse: Commit[] = [];

        if (response) {
          let result = this.modifyCommitTransaction(response, getState());

          if (result) {
            patchState({ commitTransactionDetails: result });
          }

          response.forEach((commit: Commit): void => {
            let commitWithModifiedDates = this.dateService.commitHistoryTableDates({ ...commit });
            modifiedResponse.push(commitWithModifiedDates);
          });
          patchState({
            commitFilter: filter,
            commits: modifiedResponse,
          });

          dispatch(new SetCommitsWithFilterSuccess());
        }
      },
    });
  }

  /**
   * Set Commit with Dummy Error
   * @memberof SupplyVisibilityState
   */
  @Action(SetCommitsWithDummyError)
  setCommitsWithDummyError(): void {}

  /**
   * Set Commit with Dummy Success
   * @memberof SupplyVisibilityState
   */
  @Action(SetCommitsWithDummySuccess)
  setCommitWithDummySuccess(): void {}

  /**
   * Set Commit with Dummy Error
   * @memberof SupplyVisibilityState
   */
  @Action(GetDummyCommitHeadersError)
  getDummyCommitHeadersError(): void {}

  /**
   * Set Commit with Dummy Success
   * @memberof SupplyVisibilityState
   */
  @Action(GetDummyCommitHeadersSuccess)
  getDummyCommitHeadersSuccess(): void {}

  /**
   * Set Commit Error
   * @memberof SupplyVisibilityState
   */
  @Action(SetCommitsError)
  setCommitsError(): void {}

  /**
   * Set Commit Success
   * @memberof SupplyVisibilityState
   */
  @Action(SetCommitSuccess)
  setCommitSuccess(): void {}

  /**
   * Set Commits with Dummy commits from API
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, patchState }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(GetNotifications, { cancelUncompleted: false })
  getNotifications({
    dispatch,
    patchState,
  }: StateContext<SupplyVisibilityStateModel>): Subscription {
    return this.supplyVisibilityService.getNotificationsList().subscribe({
      error: (error: unknown): void => {
        dispatch(new GetNotificationsError(error));
      },
      next: (response: ApiResponse<SVNotification[]>): void => {
        const notifications = response ? response.result || [] : [];
        patchState({ notifications });
        dispatch(new GetNotificationsSuccess(notifications));
      },
    });
  }

  @Action(GetNotificationsUnreadCount, { cancelUncompleted: false })
  getNotificationsUnreadCount({
    dispatch,
    patchState,
  }: StateContext<SupplyVisibilityStateModel>): Subscription {
    return this.supplyVisibilityService.getNotificationsUnreadCount().subscribe({
      error: (error: unknown): void => {
        console.error('Error fetching unread count', error);
      },
      next: (response: number | { count: number }): void => {
        // Handle both { count: number } and raw number response
        const count =
          response !== null && typeof response === 'object' && 'count' in response
            ? response.count
            : typeof response === 'number'
              ? response
              : 0;

        patchState({ notificationsUnreadCount: count });
        dispatch(new GetNotificationsUnreadCountSuccess(count));
      },
    });
  }

  @Action(GetNotificationDetails)
  getNotificationDetails(
    { dispatch, getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { id }: { id: string },
  ): unknown {
    this.activeFetchInfo.notificationDetailId = id;
    const state = getState();
    if (state.notificationDetailCache[id]) {
      return dispatch(new GetNotificationDetailsSuccess(state.notificationDetailCache[id]));
    }
    return this.supplyVisibilityService.getNotificationDetail(id).subscribe(
      (response: SVNotification): void => {
        const notificationDetail = response;
        const re = /,/g;
        if (notificationDetail.message) {
          notificationDetail.message = notificationDetail.message.replace(re, '<br>');
        }
        patchState({
          notificationDetailCache: { [id]: notificationDetail, ...state.notificationDetailCache },
        });

        if (this.activeFetchInfo.notificationDetailId == id) {
          dispatch(new GetNotificationDetailsSuccess(notificationDetail));

          if (!notificationDetail.isRead) {
            this.supplyVisibilityService.setNotificationAsRead(id).subscribe((): void => {
              const index = state.notifications.findIndex(
                (data: SVNotification): boolean => data.id == id,
              );
              const notifications = [...state.notifications];
              if (index !== -1) {
                notifications[index].isRead = true;
              }

              patchState({
                notifications: notifications,
              });
              if (this.activeFetchInfo.notificationDetailId == id) {
                dispatch(new GetNotificationsUnreadCount());
              }
            });
          } else {
            if (this.activeFetchInfo.notificationDetailId == id) {
              dispatch(new GetNotificationsUnreadCount());
            }
          }
        }
      },
      (_error: unknown): void => {
        dispatch(new GetNotificationDetailsError());
      },
    );
  }

  @Action(SetAllNotificationsAsRead)
  setAllNotificationAsRead({ dispatch }: StateContext<SupplyVisibilityStateModel>): Subscription {
    const errorMsg = 'Unable to mark all as read';
    return this.supplyVisibilityService.setAllNotificationsAsRead().subscribe(
      (response: boolean): void => {
        dispatch(new GetNotifications());
        dispatch(new GetNotificationsUnreadCount());
        if (!response) {
          this.notification.showError(errorMsg);
        }
      },
      (error: unknown): void => {
        this.notification.showErrorOrMessage(error, errorMsg);
      },
    );
  }

  /**
   * Set Commits from API
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, patchState }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(SetPnReviewed)
  setPnReviewed(
    { dispatch }: StateContext<SupplyVisibilityStateModel>,
    { pnReviewed }: { pnReviewed: PnReviewed },
  ): Subscription {
    return this.supplyVisibilityService.setPnReviewed(pnReviewed).subscribe({
      error: (error): void => {
        dispatch(new SetPnReviewedError(error, pnReviewed));
      },
      next: (response: PnReviewed): void => {
        if (response) {
          dispatch(new SetPnReviewedSuccess(pnReviewed));
        }
      },
    });
  }

  /**
   * Remove flag from part number
   * @param {StateContext<SupplyVisibilityStateModel>}
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(DeletePnFlags)
  deletePnFlag(
    { dispatch }: StateContext<SupplyVisibilityStateModel>,
    { flag, pnReviewed }: DeletePnFlags,
  ): Subscription {
    return this.supplyVisibilityService.deletePnFlags(pnReviewed, flag).subscribe({
      error: (error): void => {
        dispatch(new DeletePnFlagsError(error));
      },
      next: (): void => {
        dispatch(new DeletePnFlagsSuccess(pnReviewed));
      },
    });
  }

  /**
   * Add New Commit
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, patchState }
   * @param {AddCommit} { commit }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(AddCommit)
  addCommit(
    { dispatch, getState }: StateContext<SupplyVisibilityStateModel>,
    { commit }: AddCommit,
  ): Observable<Commit> | void {
    const state = getState();
    const { mmViewID, plant }: BasicParameters = state.basicParameters;
    let { vendorCode }: BasicParameters = state.basicParameters;

    if (commit) {
      // when All vendors is selected use vendor chosen in commit dialog
      if (vendorCode === undefined || vendorCode === '') {
        vendorCode = state.commitVendorCode;
      }
      const addCommit: NewCommit = {
        mmViewID,
        plant,
        vendorCode,
        ...commit,
      };

      if (commit.chooseVendor) {
        addCommit.vendorCode = commit.chooseVendor;
      }
      addCommit.partNumber = addCommit.partNumber.toUpperCase();
      addCommit.invoiceNumber = addCommit.invoiceNumber || 'NULL INVOICE';

      // fields not present in commit form
      // addCommit.manufacturer = "";
      // addCommit.countryOfOrigin = "";
      // addCommit.forwarder = "";
      // addCommit.expressFlag = "";
      // addCommit.thirdPartyPaid = "";
      // addCommit.typeOfTransportation = "";
      // addCommit.reason = 0;
      // addCommit.apsRelevant = "Y";

      return this.supplyVisibilityService.addCommit(addCommit).pipe(
        tap({
          error: (error: unknown): void => {
            dispatch(new AddCommitError(error));
          },
          next: (commit: Commit): void => {
            if (commit) {
              SupplyVisibilityState.isForecastDataChanged = false;
              dispatch(new AddCommitSuccess());
              dispatch(new AddCommitNotify(commit));
              if (!state.commitModuleOn) {
                dispatch(new SetCommitsWithDummy());
              }
            }
          },
        }),
      );
    }
  }

  /**
   * Add New Dummy Commit
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, patchState }
   * @param {AddDummyCommit} { commit }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(AddDummyCommit)
  addDummyCommit(
    { dispatch, getState }: StateContext<SupplyVisibilityStateModel>,
    { commit }: AddDummyCommit,
  ): Observable<Commit> | void {
    const state = getState();
    let { partNumber, vendorCode }: BasicParameters = state.basicParameters;

    if (!vendorCode) {
      return;
    }

    if (commit) {
      const addDummyCommit: DummyCommitDetail = {
        ...commit,
        partNumber,
        vendorCode,
      };
      // TODO - Temporary solution (no data from backend)
      addDummyCommit.asn = 0;
      if (commit.chooseVendor) {
        addDummyCommit.vendorCode = commit.chooseVendor;
      }
      return this.supplyVisibilityService.addDummyCommit(addDummyCommit).pipe(
        tap({
          error: (error: unknown): void => {
            dispatch(new AddDummyCommitError(error));
          },
          next: (commit: DummyCommitDetail): void => {
            if (commit) {
              if (!state.commitModuleOn) dispatch(new SetCommitsWithDummy());
              dispatch(new AddDummyCommitSuccess(commit));
            }
          },
        }),
      );
    }
  }

  /**
   * Add Dummy Commit Error
   * @memberof SupplyVisibilityState
   */
  @Action(AddDummyCommitError)
  addDummyCommitError(): void {}

  /**
   * Add Dummy Commit Success
   * @memberof SupplyVisibilityState
   */
  @Action(AddDummyCommitSuccess)
  addDummyCommitSuccess(): void {}

  /**
   * Add New Dummy Commit header
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, patchState }
   * @param {DummyCommitHeader} { commit }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(AddDummyCommitHeader)
  addDummyCommitHeader(
    { dispatch }: StateContext<SupplyVisibilityStateModel>,
    { commit, filter }: AddDummyCommitHeader,
  ): Subscription | void {
    if (commit) {
      return this.supplyVisibilityService.addDummyCommitHeader(commit).subscribe(
        (_res: unknown): void => {
          dispatch(new AddDummyCommitHeaderSuccess());
          dispatch(new GetDummyCommitHeaders(filter));
          dispatch(new SetDummyCommitHeader(undefined));
        },
        (error: unknown): void => {
          dispatch(new AddDummyCommitHeaderError(error));
        },
      );
    }
  }

  /**
   * Update Dummy Commit Header Error
   * @memberof SupplyVisibilityState
   */
  @Action(UpdateDummyCommitHeaderError)
  updateDummyCommitHeaderError(): void {}

  /**
   * Update Dummy Commit Header Success
   * @memberof SupplyVisibilityState
   */
  @Action(UpdateDummyCommitHeaderSuccess)
  updateDummyCommitHeaderSuccess(): void {}

  /**
   * Update Dummy Commit header
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, patchState }
   * @param {DummyCommitHeader} { header }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(UpdateDummyCommitHeader)
  updateDummyCommitHeader(
    { dispatch }: StateContext<SupplyVisibilityStateModel>,
    { filter, header }: UpdateDummyCommitHeader,
  ): Subscription | void {
    if (header) {
      return this.supplyVisibilityService.updateDummyCommitHeader(header).subscribe(
        (res: DummyCommitHeader): void => {
          dispatch(new AddDummyCommitHeaderSuccess());
          dispatch(new GetDummyCommitHeaders(filter));
          dispatch(new SetDummyCommitHeader(res));
        },
        (error: unknown): void => {
          dispatch(new AddDummyCommitHeaderError(error));
        },
      );
    }
  }
  /**
   * Delete Dummy Commit Header Error
   * @memberof SupplyVisibilityState
   */
  @Action(DeleteDummyCommitHeaderError)
  deleteDummyCommitHeaderError(): void {}

  /**
   * Delete Dummy Commit Header Success
   * @memberof SupplyVisibilityState
   */
  @Action(DeleteDummyCommitHeaderSuccess)
  deleteDummyCommitHeaderSuccess(): void {}

  /**
   * Delete Dummy Commit header
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, patchState }
   * @param {DummyCommitHeader} { header }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(DeleteDummyCommitHeader)
  deleteDummyCommitHeader(
    { dispatch }: StateContext<SupplyVisibilityStateModel>,
    { filter, header }: DeleteDummyCommitHeader,
  ): Subscription | void {
    if (header) {
      return this.supplyVisibilityService.deleteDummyCommitHeader(header).subscribe(
        (_res: unknown): void => {
          dispatch(new DeleteDummyCommitHeaderSuccess());
          dispatch(new GetDummyCommitHeaders(filter));
          dispatch(new SetDummyCommitHeader(undefined));
        },
        (error: unknown): void => {
          dispatch(new DeleteDummyCommitHeaderError(error));
        },
      );
    }
  }

  /**
   * Delete Dummy Commit Error
   * @memberof SupplyVisibilityState
   */
  @Action(DeleteDummyCommitError)
  deleteDummyCommitError(): void {}

  /**
   * Delete Dummy Commit Success
   * @memberof SupplyVisibilityState
   */
  @Action(DeleteDummyCommitSuccess)
  deleteDummyCommitSuccess(): void {}

  /**
   * Delete Dummy Commit
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, patchState }
   * @param {DummyCommitDetail} { header }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(DeleteDummyCommit)
  deleteDummyCommit(
    { dispatch }: StateContext<SupplyVisibilityStateModel>,
    { dummyCommitDetail }: DeleteDummyCommit,
  ): Subscription | void {
    if (dummyCommitDetail) {
      return this.supplyVisibilityService.deleteDummyCommit(dummyCommitDetail).subscribe(
        (_res: unknown): void => {
          dispatch(new DeleteDummyCommitSuccess());
          dispatch(new SetCommitsWithDummy());
        },
        (error: unknown): void => {
          dispatch(new DeleteDummyCommitError(error));
        },
      );
    }
  }

  /**
   * Set basis user setting key
   * @param {StateContext<SupplyVisibilityStateModel>} { patchState }
   * @param {SetUserSettingParameters} { parameters }
   * @memberof SupplyVisibilityState
   */
  @Action(SetUserSettingParameters)
  setUserSettingParameters(
    { patchState }: StateContext<SupplyVisibilityStateModel>,
    { parameters }: SetUserSettingParameters,
  ): void {
    patchState({ userSettingParameters: parameters });
  }

  /**
   * Set UserSettings from API
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, patchState }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(SetUserSettings)
  setUserSettings({
    dispatch,
    getState,
    patchState,
  }: StateContext<SupplyVisibilityStateModel>): Subscription {
    const state = getState();
    const parameters = state.userSettingParameters;

    return this.supplyVisibilityService.getUserSettings(parameters.key).subscribe({
      error: (error: unknown): void => {
        dispatch(new SetUserSettingsError(error));
      },
      next: (response: UserSettings): void => {
        if (response) {
          patchState({ userSettings: response });
          dispatch(new SetUserSettingsSuccess());
        }
      },
    });
  }

  /**
   * Set UserSetting Error
   * @memberof SupplyVisibilityState
   */
  @Action(SetUserSettingsError)
  setUserSettingsError(): void {}

  /**
   * Set UserSetting Success
   * @memberof SupplyVisibilityState
   */
  @Action(SetUserSettingsSuccess)
  setUserSettingSuccess(): void {}

  /**
   * Add New UserSetting
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, patchState }
   * @param {AddUserSettings} { userSetting }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(AddUserSettings)
  addUserSettings(
    { dispatch, patchState }: StateContext<SupplyVisibilityStateModel>,
    { updateIdInData, userSettings }: AddUserSettings,
  ): Observable<unknown> | void {
    if (userSettings) {
      const addUserSetting: NewUserSettings = userSettings;

      return this.supplyVisibilityService.addUserSettings(addUserSetting, updateIdInData).pipe(
        tap({
          error: (error: unknown): void => {
            dispatch(new AddUserSettingsError(error));
          },
          next: (userSetting: unknown): void => {
            const uSettings = userSetting as UserSettings;
            if (uSettings) {
              patchState({ userSettings: uSettings });
            }
            dispatch(new AddUserSettingsSuccess());
          },
        }),
      );
    }
  }

  /**
   * Edit New UserSetting
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, patchState }
   * @param {EditUserSettings} { userSetting }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(EditUserSettingsError)
  editUserSettingsError({ dispatch }: StateContext<SupplyVisibilityStateModel>): void {
    dispatch(new AddCommitError(new Error('Unsuccessful edit')));
  }
  @Action(EditUserSettings)
  editUserSetting(
    { dispatch, patchState }: StateContext<SupplyVisibilityStateModel>,
    { isNotificationRequired, userSettings }: EditUserSettings,
  ): Subscription | void {
    if (userSettings) {
      const editUserSetting: NewUserSettings = userSettings;
      const widgetId = userSettings.key;

      return this.supplyVisibilityService.updateUserSettings(editUserSetting).subscribe({
        error: (error: unknown): void => {
          dispatch(new EditUserSettingsError(error));
        },
        next: (_userSetting: unknown): void => {
          patchState({ newUserSettings: userSettings });
          dispatch(new EditUserSettingsSuccess(isNotificationRequired));
          dispatch(new SetPanelCacheSuccess(widgetId));
        },
      });
    }
  }

  @Action(DeleteUserSettings)
  deleteUserSettings(
    ctx: StateContext<SupplyVisibilityStateModel>,
    { key }: DeleteUserSettings,
  ): Subscription | void {
    if (!key) return;
    return this.supplyVisibilityService.deleteUserSettings(key).subscribe((): void => {
      // Logic for deleting user settings if needed
    });
  }

  /**
   * Add Commit Error
   * @memberof SupplyVisibilityState
   */
  @Action(AddCommitError)
  addCommitError(): void {}

  /**
   * Add Commit Success
   * @memberof SupplyVisibilityState
   */
  @Action(AddCommitSuccess)
  addCommitSuccess(): void {}

  /**
   * Add Commit Notify
   * @memberof SupplyVisibilityState
   */
  @Action(AddCommitNotify)
  addCommitNotify(): void {}

  /**
   * Edit existing Commit
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, dispatch }
   * @param {UpdateCommit} { commit }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(EditCommit)
  editCommit(
    { dispatch, getState }: StateContext<SupplyVisibilityStateModel>,
    { commit }: EditCommit,
  ): Observable<Commit> {
    const state = getState();
    const { mmViewID, plant, vendorCode }: BasicParameters = state.basicParameters;

    let updateCommit: NewCommit = {
      mmViewID,
      plant,
      vendorCode,
      ...commit,
    };

    if (commit.chooseVendor) {
      updateCommit.vendorCode = commit.chooseVendor;
    }

    if (!updateCommit.status) {
      updateCommit.status = '';
      console.log('adding status');
    }
    if (!updateCommit.order) {
      updateCommit.order = '';
      console.log('adding order');
    }
    if (!updateCommit.parentID) {
      updateCommit.parentID = '';
      console.log('adding parentID');
    }
    if (!updateCommit.countryOfOrigin) {
      updateCommit.countryOfOrigin = '';
    }
    if (!updateCommit.manufacturer) {
      updateCommit.manufacturer = '';
    }

    return this.supplyVisibilityService.updateCommit(updateCommit).pipe(
      tap({
        error: (error: unknown): void => {
          dispatch(new EditCommitError(error));
        },
        next: (commit): void => {
          if (commit) {
            SupplyVisibilityState.isForecastDataChanged = false;
            //For N type commit, for commit whose PO number is changed and for remerged commit (type edit is used)
            if (
              updateCommit.status == 'N' ||
              updateCommit['WithChangePurchaseOrderNumber'] ||
              updateCommit['withRemerge']
            ) {
              let modifiedCommitTransactions = state.commitTransactionDetails.filter(
                (commit): boolean => commit.type != EnumCommitType.EDIT,
              );
              let commitTransaction: CommitTransactionDetails = {
                data: commit,
                datagridInfo: {
                  index: 0,
                  isPageNumberNeeded:
                    updateCommit['WithChangePurchaseOrderNumber'] || updateCommit['withRemerge'],
                },
                id: commit.inboundDeliveryNumber as string,
                isCache:
                  updateCommit['WithChangePurchaseOrderNumber'] || updateCommit['withRemerge']
                    ? false
                    : true,
                type: EnumCommitType.EDIT,
              };
              modifiedCommitTransactions.push(commitTransaction);
              dispatch(new PatchCommitTransactionDetails(modifiedCommitTransactions));
            } else {
              const updatedCommitTransactions = state.commitTransactionDetails.map(
                (stateCommit): CommitTransactionDetails =>
                  stateCommit.id == commit.inboundDeliveryNumber
                    ? { ...stateCommit, data: commit }
                    : stateCommit,
              );
              dispatch(new PatchCommitTransactionDetails(updatedCommitTransactions));
            }

            dispatch(new EditCommitSuccess(commit));
          }
        },
      }),
    );
  }
  /**
   * Edit existing Commit
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, dispatch }
   * @param {DeleteCommit} { commit }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(DeleteCommit)
  deleteCommit(
    { dispatch }: StateContext<SupplyVisibilityStateModel>,
    { baseModule, commit, lowerTriggerUrl }: DeleteCommit,
  ): Subscription {
    const triggerUrl = !lowerTriggerUrl ? '' : lowerTriggerUrl;
    if (baseModule == Helper.COMMIT_MODULE) {
      return this.commitsService.deleteCommit(commit, triggerUrl).subscribe({
        error: (error: unknown): void => {
          dispatch(new DeleteCommitError(error));
        },
        next: (_commit): void => {
          dispatch(new DeleteCommitSuccess());
        },
      });
    } else {
      return this.commitsService.deleteCommit(commit, triggerUrl).subscribe({
        error: (error: unknown): void => {
          dispatch(new DeleteCommitError(error));
          dispatch(new SetCommitsWithDummy());
        },
        next: (_commit): void => {
          SupplyVisibilityState.isForecastDataChanged = false;
          dispatch(new DeleteCommitSuccess());
          dispatch(new SetCommitsWithDummy());
        },
      });
    }
  }

  /**
   * Cancel Commit
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, dispatch }
   * @param {CancelCommit} { commit }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(CancelCommit)
  cancelCommit(
    { dispatch }: StateContext<SupplyVisibilityStateModel>,
    { commit }: CancelCommit,
  ): Subscription {
    return this.supplyVisibilityService.cancelCommit(commit).subscribe({
      error: (error: unknown): void => {
        dispatch(new CancelCommitError(error));
      },
      next: (data: unknown): void => {
        if (data) {
          dispatch(new CancelCommitSuccess());
        }
      },
    });
  }

  /**
   * Delete PLO
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, dispatch }
   * @param {DeletePLO} { commit }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(DeletePLO)
  deletePLO(
    { dispatch }: StateContext<SupplyVisibilityStateModel>,
    { commit }: DeletePLO,
  ): Subscription {
    return this.supplyVisibilityService.deletePLO(commit).subscribe({
      error: (error: unknown): void => {
        dispatch(new DeletePLOError(error));
      },
      next: (data: unknown): void => {
        if (data) {
          dispatch(new DeletePLOSuccess());
        }
      },
    });
  }

  /**
   * Edit existing DUmmy Commit
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, dispatch }
   * @param {EditDummyCommit} { commit }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(EditDummyCommit)
  editDummyCommit(
    { dispatch, getState }: StateContext<SupplyVisibilityStateModel>,
    { commit }: EditDummyCommit,
  ): Observable<Commit> | void {
    const state = getState();
    const { partNumber, vendorCode }: BasicParameters = state.basicParameters;

    if (!vendorCode) {
      return;
    }

    const updateDummyCommit: DummyCommitDetail = {
      ...commit,
      partNumber,
      vendorCode,
    };
    // TODO - Temporary solution (no data from backend)
    updateDummyCommit.asn = 0;

    return this.supplyVisibilityService.updateDummyCommit(updateDummyCommit).pipe(
      tap({
        error: (error: unknown): void => {
          dispatch(new EditDummyCommitError(error));
        },
        next: (commit: Commit): void => {
          if (commit) {
            dispatch(new EditDummyCommitSuccess());
            dispatch(new SetCommitsWithDummy());
          }
        },
      }),
    );
  }

  /**
   * Get Purchase Orders from API
   * @param {StateContext<SupplyVisibilityStateModel>} { patchState }
   * @param {SetPurchaseOrders} { parameters }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(SetPurchaseOrders)
  setPurchaseOrders({
    dispatch,
    getState,
    patchState,
  }: StateContext<SupplyVisibilityStateModel>): Subscription {
    const state = getState();
    const parameters: BasicParameters = Helper.createCopy(state.basicParameters);
    let poParameters: BasicParameters = parameters || state.basicParameters;

    if (poParameters.vendorCode === undefined || poParameters.vendorCode === '') {
      poParameters = { ...poParameters, vendorCode: state.commitVendorCode };
    }
    return this.supplyVisibilityService
      .getPurchaseOrders(poParameters)
      .subscribe((response: PurchaseOrders[]): void => {
        patchState({ purchaseOrders: [...response] });
        dispatch(new SetPurchaseOrdersSuccess());
      });
  }

  /**
   * Get Purchase Orders from API
   * @param {StateContext<SupplyVisibilityStateModel>} { patchState }
   * @param {SetPurchaseOrdersForCM} { parameters }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(SetPurchaseOrdersForCM)
  setPurchaseOrdersForCM(
    { dispatch, getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { parameters }: SetPurchaseOrdersForCM,
  ): Subscription {
    const state = getState();
    let poParameters = parameters as BasicParameters;

    if (parameters?.vendorCode === undefined || parameters.vendorCode === '') {
      poParameters = { ...parameters, vendorCode: state.commitVendorCode } as BasicParameters;
    }
    return this.supplyVisibilityService
      .getPurchaseOrders(poParameters)
      .subscribe((response: PurchaseOrders[]): void => {
        patchState({ purchaseOrders: [...response] });
        dispatch(new SetPurchaseOrdersSuccess());
      });
  }
  /**
   * Set basis URL parameters (PLANT|PARTNUMBER|VEDORCODE)
   * @param {StateContext<SupplyVisibilityStateModel>} { patchState }
   * @param {SetBasicParameters} { parameters }
   * @memberof SupplyVisibilityState
   */
  @Action(SetBasicParameters)
  setBasicParameters(
    { patchState }: StateContext<SupplyVisibilityStateModel>,
    { parameters }: SetBasicParameters,
  ): void {
    patchState({
      basicParameters: {
        mmViewID: parameters.mmViewID,
        partNumber: parameters.partNumber,
        plant: parameters.plant,
        records: parameters.records,
        variant: parameters.variant,
        vendorCode: parameters.vendorCode,
      },
    });
  }
  /**
   * Set basis URL parameters (PLANT|PARTNUMBER|VEDORCODE)
   * @param {StateContext<SupplyVisibilityStateModel>} { patchState }
   * @param {SetPartNumberListParameters} { parameters }
   * @memberof SupplyVisibilityState
   */
  @Action(SetPartNumberListParameters)
  setPartNumberListParameters(
    { dispatch, patchState }: StateContext<SupplyVisibilityStateModel>,
    { pnList, widgetId }: SetPartNumberListParameters,
  ): void {
    if (pnList && pnList.length > 0) {
      patchState({
        partNumberListParameters: { [widgetId]: pnList },
      });
      dispatch(new SetPartNumberListParametersSuccess(widgetId, pnList));
    }
  }
  @Action(SetActiveVendor)
  setActiveVendor(
    { getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { vendor }: SetActiveVendor,
  ): void {
    const state = getState();
    const parameters: BasicParameters = state.basicParameters;

    patchState({
      basicParameters: {
        ...parameters,
        vendorCode: vendor,
      },
    });
  }

  /**
   * Set Date Range parameters from API
   * @param {StateContext<SupplyVisibilityStateModel>} { patchState }
   * @param {SetDateRangeParameters} { parameters }
   * @memberof SupplyVisibilityState
   */
  @Action(SetDateRangeParameters)
  setDateRangeParameters(
    { dispatch, patchState }: StateContext<SupplyVisibilityStateModel>,
    { parameters }: SetDateRangeParameters,
  ): void {
    let dateRange: DateRangeParameters;

    if (!parameters) {
      dateRange = Helper.getCommitHistoryDateRange();
      dispatch(new SetDateRangeParameters(dateRange));
      return;
    } else {
      dateRange = parameters;
    }

    const tDeliveryDateFrom = this.supplyVisibilityService.convertDate(dateRange, 'dateFrom');
    const tDeliveryDateTo = this.supplyVisibilityService.convertDate(dateRange, 'dateTo');
    const tDateRange = {
      dateFrom: tDeliveryDateFrom,
      dateTo: tDeliveryDateTo,
    };
    patchState({
      daterangeParameters: tDateRange,
    });
    dispatch(new SetDateRangeParametersSuccess(tDateRange));
    // dispatch(new SetCommitsWithDummy());
  }

  /**
   * Set Date Range parameters from API
   * @param {StateContext<SupplyVisibilityStateModel>} { patchState }
   * @param {SetDummyCommitHeader} { header }
   * @memberof SupplyVisibilityState
   */
  @Action(GetDummyCommitHeaders)
  getDummyCommitHeaders(
    { dispatch, patchState }: StateContext<SupplyVisibilityStateModel>,
    { filter }: GetDummyCommitHeaders,
  ): Observable<DummyCommitHeader[]> | void {
    if (filter) {
      return this.supplyVisibilityService.getDummyCommitHeaders(filter).pipe(
        tap({
          error: (error: unknown): void => {
            dispatch(new GetDummyCommitHeadersError(error));
          },
          next: (res: DummyCommitHeader[]): void => {
            patchState({ dummyCommitsHeaders: res });
            dispatch(new GetDummyCommitHeadersSuccess());
          },
        }),
      );
    }
  }

  @Action(SetDummyCommitHeader)
  setDummyCommitHeader(
    { patchState }: StateContext<SupplyVisibilityStateModel>,
    { header }: SetDummyCommitHeader,
  ): void {
    patchState({
      selectedDummyCommitHeader: header,
    });
  }

  @Action(TriggerLinkEventCommitDialog)
  triggerLinkEventCommitDialog(): void {
    // This action is handled in the component, not the state.
    // The state only dispatches it.
  }

  /**
   * Set Vendor Codes
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, patchState }
   * @param partNumbers
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(SetVendorCodes)
  setVendorCodes(
    { dispatch, patchState }: StateContext<SupplyVisibilityStateModel>,
    { partNumbers }: SetVendorCodes,
  ): Observable<VendorCode[]> | void {
    return this.supplyVisibilityService.getVendorCodeNamesList(partNumbers).pipe(
      switchMap((response: PartNumberVendorCodes[]): Observable<VendorCode[]> => {
        try {
          const transformedInitial = transformVendorCodesResponse(response);

          // detect if vendor codes are plain strings (no names)
          const anyStringOnly = transformedInitial.some((t): boolean =>
            (t.vendorCodes || []).some(
              (vc): boolean => typeof vc === 'string' && vc.indexOf('|') === -1,
            ),
          );

          if (!anyStringOnly) {
            return of(transformedInitial);
          }

          return this.commitsService.getVendorsWithNames(false).pipe(
            map((vendors: VendorName[] | { result: VendorName[] }): VendorCode[] => {
              const vendorsList = normalizeVendorsList(vendors);
              const nameMap = buildVendorNameMap(vendorsList);
              return transformItemsWithVendorNames(transformedInitial, nameMap);
            }),
          );
        } catch {
          return of([]);
        }
      }),
      tap({
        error: (error: unknown): void => {
          dispatch(new SetVendorCodesError(error));
        },
        next: (codes: VendorCode[]): void => {
          patchState({
            vendorCodes: codes,
          });
          dispatch(new SetVendorCodesSuccess());
        },
      }),
    );
  }

  /**
   * Set selected vendor code for new commit
   * @param {StateContext<SupplyVisibilityStateModel>} { patchState }
   * @param {SetCommitVendorCode} { vendorCode }
   * @memberof SupplyVisibilityState
   */
  @Action(SetCommitVendorCodeSuccess)
  setCommitVendorCodeSuccess(): void {}
  @Action(SetCommitVendorCode)
  setCommitVendorCode(
    { dispatch, patchState }: StateContext<SupplyVisibilityStateModel>,
    { vendorCode }: SetCommitVendorCode,
  ): void {
    patchState({
      commitVendorCode: vendorCode,
    });
    dispatch(new SetCommitVendorCodeSuccess());
  }

  /**
   * Set selected vendor code for new commit
   * @param {StateContext<SupplyVisibilityStateModel>} { patchState }
   * @param {SetCommitVendorCode} { vendorCode }
   * @memberof SupplyVisibilityState
   */
  @Action(SetCommitModuleOn)
  setCommitModuleOn(
    { patchState }: StateContext<SupplyVisibilityStateModel>,
    { isCommitModule: isCommitModuleOn }: SetCommitModuleOn,
  ): void {
    patchState({
      commitModuleOn: isCommitModuleOn,
    });
  }

  /**
   * Set Forecast data
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, patchState }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(SetForecast)
  async setForecast(
    { dispatch, getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { id }: SetForecast,
  ): Promise<Subscription | void> {
    this.activeFetchInfo.forecast.currentPnId = id as string;
    const state = getState();
    const parameters: BasicParameters = Helper.createCopy(state.basicParameters);
    const dummyCommitHeader = state.selectedDummyCommitHeader;

    // check if we have cached forecast
    const cachedForecast = state.cachedForecast;

    // if used more than one place, take it in other state's property and use it instead of sending in success action
    // when All vendors is selected use PN-Vendor array
    if (
      parameters &&
      (parameters.vendorCode === null ||
        parameters.vendorCode === undefined ||
        parameters.vendorCode === '')
    ) {
      return dispatch(new SetVendorCodes([parameters.partNumber])).subscribe(
        async (): Promise<void> => {
          const state = getState();
          // console.log('state.vendorCodes', state.vendorCodes);
          const vendorCodes = state.vendorCodes;
          const vendorList: Array<{ partNumber: string; vendorCode: string }> = [];
          vendorCodes.forEach((element): void => {
            element.vendorCodes.forEach((vendorCode): void => {
              vendorList.push({
                partNumber: element.partNumber,
                vendorCode: vendorCode,
              });
            });
          });
          // create hash for parameters
          const parametersForHash = [parameters, dummyCommitHeader, vendorList];
          const parametersHash = objectHash.default(parametersForHash);
          const cachedForecastHash = state.cachedForecastHash;

          if (cachedForecast !== null && parametersHash === cachedForecastHash) {
            // console.log('Returning cached forecast...');
            if (
              !this.isValidPn(
                getState(),
                cachedForecast?.partNumber as string,
                cachedForecast?.vendorCode as string,
              )
            ) {
              return;
            }
            patchState({
              cachedForecast: undefined,
              cachedForecastHash: '',
              forecast: cachedForecast,
            });
            // without timeout catchSetCommitsWithDummySuccess in commit-history.component.ts isn't caught
            setTimeout((): void => {
              dispatch(new SetForecastSuccess(id));
            }, 100);
          } else {
            // console.log('Fetching forecast...>>', parameters.partNumber, parameters.vendorCode);
            // console.log('vendorList', vendorList);
            if (this.forecastSubs) {
              this.forecastSubs.unsubscribe();
            }

            this.forecastSubs = this.supplyVisibilityService
              .getForecastAllVendors(parameters, dummyCommitHeader, vendorList)
              .pipe(debounceTime(1000))
              .subscribe({
                error: (error: unknown): void => {
                  dispatch(new SetForecastError(error));
                },
                next: async (res: unknown): Promise<void> => {
                  const forecast = res as Forecast;
                  if (
                    forecast &&
                    forecast.projectionDetails &&
                    forecast.projectionDetails.length > 0
                  ) {
                    if (!this.isValidPn(getState(), forecast.partNumber, forecast.vendorCode)) {
                      return;
                    }
                    dispatch(new SetForecastSuccess(id));
                    patchState({
                      forecast: {
                        materialManagementViewID: forecast.materialManagementViewID,
                        mergeProjections:
                          forecast.mergeProjections && forecast.mergeProjections?.length > 0
                            ? forecast.mergeProjections
                            : [],
                        partNumber: forecast.partNumber,
                        plant: forecast.plant,
                        projectionDetails: forecast.projectionDetails,
                        records: forecast.records,
                        reviewed: forecast.reviewed, //TODO Change dependency
                        staticvalues: forecast.staticvalues,
                        variant: forecast.variant,
                        vendorCode: forecast.vendorCode,
                      },
                    });
                  } else {
                    dispatch(new SetForecastError());
                  }
                },
              });
            // return this.forecastSubs;
          }

          // if (this.isVirtualVC(state)) {
          //   let childrenPNList = await this.getChildrenPNList(parameters)
          //   dispatch(new SetChildrenPNList(parameters, id));
          //   cachedChildrenForecast = await this.setChildrenForecast(state, parameters, childrenPNList);

          //   if (id != this.activeFetchInfo.forecast.currentPnId) { return }
          //   dispatch(new SetChildrenForecastSuccess(cachedChildrenForecast));
          // }
        },
      );
    } else {
      // create hash for parameters
      const parametersForHash = [parameters, dummyCommitHeader];
      const parametersHash = objectHash.default(parametersForHash);
      const cachedForecastHash = state.cachedForecastHash;
      console.log('parametersHash SetForecast', parametersHash);

      if (cachedForecast !== null && parametersHash === cachedForecastHash) {
        if (
          !this.isValidPn(
            getState(),
            cachedForecast?.partNumber as string,
            cachedForecast?.vendorCode as string,
          )
        ) {
          return;
        }
        console.log('Returning cached forecast...');
        patchState({ cachedForecast: undefined, cachedForecastHash: '', forecast: cachedForecast });
        // without timeout catchSetCommitsWithDummySuccess in commit-history.component.ts isn't caught
        setTimeout((): void => {
          dispatch(new SetForecastSuccess(id));
        }, 100);
      } else {
        if (this.forecastSubs) {
          this.forecastSubs.unsubscribe();
        }
        console.log('Fetching forecast...>>', parameters.partNumber, parameters.vendorCode);
        this.forecastSubs = this.supplyVisibilityService
          .getForecast(parameters, dummyCommitHeader)
          .pipe(debounceTime(500))
          .subscribe({
            error: (error: unknown): void => {
              dispatch(new SetForecastError(error));
            },
            next: async (forecast: Forecast): Promise<void> => {
              if (forecast && forecast.projectionDetails && forecast.projectionDetails.length > 0) {
                if (!this.isValidPn(getState(), forecast.partNumber, forecast.vendorCode)) {
                  return;
                }
                patchState({
                  forecast: {
                    materialManagementViewID: forecast.materialManagementViewID,
                    mergeProjections:
                      forecast.mergeProjections && forecast.mergeProjections.length > 0
                        ? forecast.mergeProjections
                        : [],
                    partNumber: forecast.partNumber,
                    plant: forecast.plant,
                    projectionDetails: forecast.projectionDetails,
                    reviewed: forecast.reviewed,
                    staticvalues: forecast.staticvalues,
                    variant: forecast.variant,
                    vendorCode: forecast.vendorCode,
                  },
                });
                dispatch(new SetForecastSuccess(id));
              } else {
                dispatch(new SetForecastError());
              }
            },
          });

        // return this.forecastSubs;
      }

      // if (this.isVirtualVC(state)) {
      //   let childrenPNList = await this.getChildrenPNList(parameters);
      //   dispatch(new SetChildrenPNList(parameters, id));
      //   cachedChildrenForecast = await this.setChildrenForecast(state, parameters, childrenPNList);
      //   if (id != this.activeFetchInfo.forecast.currentPnId) { return }

      //   dispatch(new SetChildrenForecastSuccess(cachedChildrenForecast));
      // }
    }
    // }
  }

  @Action(SetChildrenPNList)
  setChildrenPnLIst(
    { getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { id, parameters }: SetChildrenPNList,
  ): void {
    //Checking if the PN is changed
    const state = getState();
    if (id == this.activeFetchInfo.forecast.currentPnId) {
      patchState({ childrenPNList: state.forecast.mergeProjections });
    } else {
      patchState({ childrenPNList: undefined });
    }
    patchState({
      childrenPNInfo: {
        childrenPNList: state.forecast.mergeProjections as Forecast[],
        parentPnInfo: parameters,
      },
    });
  }

  /**
   * Set Forecast Error
   * @memberof SupplyVisibilityState
   */
  @Action(SetForecastError)
  setForecastError(): void {}

  /**
   * Set Forecast Success
   * @memberof SupplyVisibilityState
   */
  @Action(SetForecastSuccess)
  setForecastSuccess(
    { dispatch, getState }: StateContext<SupplyVisibilityStateModel>,
    { id }: SetForecastSuccess,
  ): Observable<{ data: ForecastVirtualVc[] }> | void {
    const state = getState();
    const parameters: BasicParameters = Helper.createCopy(state.basicParameters);

    if (this.isVirtualVC(state)) {
      dispatch(new SetChildrenPNList(parameters, id as string));
      return from(this.setChildrenForecast(state, parameters)).pipe(
        tap((cachedChildrenForecast): void => {
          if (id != this.activeFetchInfo.forecast.currentPnId) {
            return;
          }
          dispatch(new SetChildrenForecastSuccess(cachedChildrenForecast));
        }),
      );
    }
  }

  /**
   * Cache Forecast data
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, patchState }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(CacheForecast)
  cacheForecast(
    { dispatch, getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { cacheParameters }: CacheForecast,
  ): Observable<unknown> | Subscription {
    this.activeFetchInfo.forecast.nextPnId = cacheParameters.id as string;

    const state = getState();
    const parameters: BasicParameters = Helper.createCopy(state.basicParameters) as BasicParameters;

    parameters.partNumber = cacheParameters.partNumber;
    parameters.vendorCode = cacheParameters.vendorCode;
    const dummyCommitHeader = Helper.createCopy(state.selectedDummyCommitHeader);

    // when All vendors is selected use PN-Vendor array
    if (
      parameters.partNumber !== undefined &&
      (parameters.vendorCode === null ||
        parameters.vendorCode === undefined ||
        parameters.vendorCode === '')
    ) {
      return dispatch(new SetVendorCodes([parameters.partNumber])).pipe(
        switchMap((): Observable<unknown> => {
          const stateAfterSet = getState();
          const vendorCodes = stateAfterSet.vendorCodes;
          const vendorList: Array<{ partNumber: string; vendorCode: string }> = [];
          vendorCodes.forEach((element: VendorCode): void => {
            element.vendorCodes.forEach((vendorCode: string): void => {
              vendorList.push({
                partNumber: element.partNumber,
                vendorCode: vendorCode,
              });
            });
          });
          // create hash for parameters
          const parametersForHash = [parameters, dummyCommitHeader, vendorList];
          const parametersHash = objectHash.default(parametersForHash);
          return this.supplyVisibilityService
            .getForecastAllVendors(parameters, dummyCommitHeader, vendorList)
            .pipe(
              tap({
                error: (error: unknown): void => {
                  dispatch(new CacheForecastError(error));
                },
                next: (res: unknown): void => {
                  const forecast = res as Forecast;
                  if (
                    forecast &&
                    forecast.projectionDetails &&
                    forecast.projectionDetails.length > 0
                  ) {
                    patchState({
                      cachedForecast: {
                        days: forecast.records,
                        materialManagementViewID: forecast.materialManagementViewID,
                        mergeProjections:
                          forecast.mergeProjections && forecast.mergeProjections.length > 0
                            ? forecast.mergeProjections
                            : [],
                        partNumber: forecast.partNumber,
                        plant: forecast.plant,
                        projectionDetails: forecast.projectionDetails,
                        reviewed: forecast.reviewed, //TODO Change dependency
                        staticvalues: forecast.staticvalues,
                        vendorCode: forecast.vendorCode,
                        weeks: forecast.records,
                      },
                      cachedForecastHash: parametersHash,
                    });
                    dispatch(new CacheForecastSuccess(cacheParameters));
                  } else {
                    dispatch(new CacheForecastError());
                  }
                },
              }),
            );
        }),
      );
    } else {
      // create hash for parameters
      const parametersForHash = [parameters, dummyCommitHeader];
      const parametersHash = objectHash.default(parametersForHash);
      return this.supplyVisibilityService.getForecast(parameters, dummyCommitHeader).subscribe({
        error: (error: unknown): void => {
          dispatch(new CacheForecastError(error));
        },
        next: (res: unknown): void => {
          const forecast = res as Forecast;
          if (this.activeFetchInfo.forecast.nextPnId != parameters.id) {
            return;
          }

          if (forecast && forecast.projectionDetails && forecast.projectionDetails.length > 0) {
            patchState({
              cachedForecast: {
                days: forecast.days,
                materialManagementViewID: forecast.materialManagementViewID,
                mergeProjections:
                  forecast.mergeProjections && forecast.mergeProjections.length > 0
                    ? forecast.mergeProjections
                    : [],
                partNumber: forecast.partNumber,
                plant: forecast.plant,
                projectionDetails: forecast.projectionDetails,
                reviewed: forecast.reviewed,
                staticvalues: forecast.staticvalues,
                vendorCode: forecast.vendorCode,
                weeks: forecast.records,
              },
              cachedForecastHash: parametersHash,
            });
            dispatch(new CacheForecastSuccess(cacheParameters));
          } else {
            dispatch(new CacheForecastError());
          }
        },
      });
    }
  }

  /**
   * Cache Forecast Error
   * @memberof SupplyVisibilityState
   */
  @Action(CacheForecastError)
  cacheForecastError(): void {}

  /**
   * Cache Forecast Success
   * @memberof SupplyVisibilityState
   */
  @Action(CacheForecastSuccess)
  cacheForecastSuccess(
    { getState }: StateContext<SupplyVisibilityStateModel>,
    { cacheParameters }: CacheForecastSuccess,
  ): Observable<unknown> | void {
    if (cacheParameters.vendorCode == 'VirtualVC') {
      const state = getState();
      if (state.cachedForecast) {
        const virtualChildList: Forecast[] = state.cachedForecast.mergeProjections as Forecast[];
        return from(
          this.supplyVisibilityService.getChildrenPnForecast(cacheParameters, virtualChildList),
        ).pipe(
          tap((virtualVcForecasts: ForecastVirtualVc[]): void => {
            if (this.activeFetchInfo.forecast.nextPnId != (cacheParameters.id as string)) {
              return;
            }
            if (cacheParameters.partNumber) {
              this.forecastsData[cacheParameters.partNumber] = { data: virtualVcForecasts };
            }
          }),
        );
      }
    }
  }

  /**
   * Set Forecast Week Range
   * @param {StateContext<SupplyVisibilityStateModel>} { patchState }
   * @param {SetForecastWeekRange} { start, end }
   * @memberof SupplyVisibilityState
   */
  @Action(SetForecastWeekRange)
  setForecastWeekRange(
    { patchState }: StateContext<SupplyVisibilityStateModel>,
    { end, start }: SetForecastWeekRange,
  ): void {
    patchState({
      forecastWeekRange: {
        endWeek: end,
        startWeek: start,
      },
    });
  }

  @Action(SetBuyersPartNumbers)
  setBuyersPartNumbers(
    { patchState }: StateContext<SupplyVisibilityStateModel>,
    { buyer }: SetBuyersPartNumbers,
  ): Observable<BuyersPartnumbers[]> {
    return from(this.supplyVisibilityBuyersPartNumbersService.getBuyersPartnumbersList(buyer)).pipe(
      tap({
        error: (error: unknown): void => {
          console.log(error);
        },
        next: (res: BuyersPartnumbers[]): void => {
          patchState({ buyersPartNumbers: res });
        },
      }),
    );
  }
  // // MaterialManagement /SupplyVisibilityReadOnly/GetPNsPrecalculateProjectionByFilter

  //   // f.e.
  //   // MaterialManagement/SupplyVisibilityReadOnly/GetPNsPrecalculateProjectionByFilter?filter=ForecastQty(w2_w8) > 100000 OR HOI > 2 AND FOI > 500
  @Action(GetPNsPrecalculateProjectionByFilter)
  getPNsPrecalculateProjectionByFilter(
    ctx: StateContext<SupplyVisibilityStateModel>,
    { filter }: GetPNsPrecalculateProjectionByFilter,
  ): Observable<BuyersPartnumbersResult> {
    return this.supplyVisibilityBuyersPartNumbersService.getPNsPrecalculateProjectionByFilter(
      filter,
    );
  }

  @Action(SetSVComments)
  setSVComments(
    { dispatch, getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { partnumber, validatePN, vendor }: SetSVComments,
  ): Observable<ApiResponse<SVComments[]>> {
    return this.supplyVisibilityCommentService.getSVCommentsByVendorPN(vendor, partnumber).pipe(
      tap({
        error: (error: unknown): void => {
          dispatch(new SetSVCommentsError(error));
        },
        next: (res: ApiResponse<SVComments[]>): void => {
          if (!validatePN) {
            if (!this.isValidPn(getState(), partnumber, vendor)) {
              return;
            }
          }
          patchState({
            svComments: res.result || [],
          });
          dispatch(new SetSVCommentsSuccess(res.result || []));
        },
      }),
    );
  }

  @Action(CreateSVComment)
  createSVComments(
    { dispatch, getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { data }: CreateSVComment,
  ): Subscription {
    const state = getState().svComments;

    return this.supplyVisibilityCommentService.createSVComment(data).subscribe({
      error: (error: unknown): void => {
        dispatch(new CreateSVCommentError(error));
      },
      next: (res: SVComments): void => {
        const result = [...state, res];
        patchState({
          svComments: result,
        });
        dispatch(new CreateSVCommentSuccess(res));
      },
    });
  }

  @Action(UpdateSVComment)
  updateSVComments(
    { dispatch, setState }: StateContext<SupplyVisibilityStateModel>,
    { data }: UpdateSVComment,
  ): Subscription {
    return this.supplyVisibilityCommentService.updateSVComment(data).subscribe({
      error: (error: unknown): void => {
        dispatch(new UpdateSVCommentError(error));
      },
      next: (res: SVComments): void => {
        setState(
          patch({
            svComments: updateItem<SVComments>((c: SVComments): boolean => c.id === data.id, res),
          }),
        );
        dispatch(new UpdateSVCommentSuccess(res));
      },
    });
  }

  @Action(DeleteSVComment)
  deleteSVComments(
    { dispatch, getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { data }: DeleteSVComment,
  ): Observable<SVComments> {
    const svComments = getState().svComments;

    return this.supplyVisibilityCommentService.deleteSVComment(data).pipe(
      tap({
        error: (error: unknown): void => {
          dispatch(new DeleteSVCommentError(error));
        },
        next: (res: SVComments): void => {
          const result = svComments.filter((q: SVComments): boolean => q.id !== res.id);
          patchState({
            svComments: result,
          });
          dispatch(new DeleteSVCommentSuccess(res));
        },
      }),
    );
  }

  @Action(SetSVPredefinedComments)
  setSVPredefinedComments(
    { patchState }: StateContext<SupplyVisibilityStateModel>,
    { category }: SetSVPredefinedComments,
  ): Subscription {
    return this.supplyVisibilityPredefinedCommentService
      .getSVCommentsByCategory(category)
      .subscribe((res: ApiResponse<SVPredefinedComments[]>): void => {
        patchState({
          svPredefinedComments: res.result || [],
        });
      });
  }

  @Action(CreateSVPredefinedComment)
  createSVPredefinedComments(
    { getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { data }: CreateSVPredefinedComment,
  ): Subscription {
    const state = getState().svPredefinedComments;

    return this.supplyVisibilityPredefinedCommentService
      .createSVPredefinedComment(data)
      .subscribe((res: SVPredefinedComments): void => {
        const result = [...state, res];
        patchState({
          svPredefinedComments: result,
        });
      });
  }

  @Action(UpdateSVPredefinedComment)
  updateSVPredefinedComments(
    { setState }: StateContext<SupplyVisibilityStateModel>,
    { data }: UpdateSVPredefinedComment,
  ): Subscription {
    return this.supplyVisibilityPredefinedCommentService
      .updateSVPredefinedComment(data)
      .subscribe((res: SVPredefinedComments): void => {
        setState(
          patch({
            svPredefinedComments: updateItem<SVPredefinedComments>(
              (c: SVPredefinedComments): boolean => c.id === data.id,
              res,
            ),
          }),
        );
      });
  }

  @Action(DeleteSVPredefinedComment)
  deleteSVPredefinedComments(
    { getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { data }: DeleteSVPredefinedComment,
  ): Subscription {
    const svPredefinedComments = getState().svPredefinedComments;

    return this.supplyVisibilityPredefinedCommentService
      .deleteSVPredefinedComment(data)
      .subscribe((res: string): void => {
        const result = svPredefinedComments.filter(
          (q: SVPredefinedComments): boolean => q.id !== res,
        );
        patchState({
          svPredefinedComments: result,
        });
      });
  }

  @Action(SetSVNotesByRecord)
  setSVNotesByRecord(
    { dispatch, getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { key, modul, searchText, skip, top }: SetSVNotesByRecord,
  ): Subscription {
    const state = getState().svNotes;

    return this.supplyVisibilityNotesService
      .getSVNotesByRecord(modul, key, searchText, top, skip)
      .subscribe({
        error: (error: unknown): void => {
          dispatch(new SetSVNotesByRecordError(error));
        },
        next: (res: SVNotesResult): void => {
          if (
            'VC:' +
              getState().basicParameters.vendorCode +
              ';PN:' +
              getState().basicParameters.partNumber !=
            key
          ) {
            return;
          }
          dispatch(new SetSVNotesByRecordSuccess());

          let result: SVNotes[] = [];
          if (skip === 0) {
            result = res.result || [];
          } else {
            result = [...state, ...(res.result || [])];
          }

          if (
            'VC:' +
              getState().basicParameters.vendorCode +
              ';PN:' +
              getState().basicParameters.partNumber !=
            key
          ) {
            return;
          }
          patchState({
            svNotes: result,
            svNotesCount: res.count,
          });
        },
      });
  }
  /**
   * Set SV Notes Error
   * @memberof SupplyVisibilityState
   */
  @Action(SetSVNotesByRecordError)
  setSVNotesByRecordError(): void {}

  @Action(CreateSVNote)
  createSVNote(
    { dispatch, getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { data }: CreateSVNote,
  ): Subscription {
    const state = getState().svNotes;

    return this.supplyVisibilityNotesService.createSVNote(data).subscribe((res: SVNotes): void => {
      const result = [...state, res];
      patchState({
        svNotes: result,
      });

      dispatch(new CreateSVNoteSuccess());
    });
  }

  /**
   * Set Create SV Note Success
   * @memberof SupplyVisibilityState
   */
  @Action(CreateSVNoteSuccess)
  createSVNoteSuccess(): void {}

  @Action(UpdateSVNote)
  updateSVNote(
    { setState }: StateContext<SupplyVisibilityStateModel>,
    { data }: UpdateSVNote,
  ): Subscription {
    return this.supplyVisibilityNotesService.updateSVNote(data).subscribe((res: SVNotes): void => {
      setState(
        patch({
          svNotes: updateItem<SVNotes>((c: SVNotes): boolean => c.id === data.id, res),
        }),
      );
    });
  }

  @Action(DeleteSVNote)
  deleteSVNote(
    { getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { data }: DeleteSVNote,
  ): Subscription {
    const svNotes = getState().svNotes;

    return this.supplyVisibilityNotesService.deleteSVNote(data).subscribe((res: SVNotes): void => {
      const result = svNotes.filter((q: SVNotes): boolean => q.id !== res.id);
      patchState({
        svNotes: result,
      });
    });
  }

  /**
   * Set Material Management Views
   * @param {StateContext<SupplyVisibilityStateModel>} { patchState }
   * @memberof SupplyVisibilityState
   */
  @Action(SetMaterialManagementViews)
  setMaterialManagementviews(
    { patchState }: StateContext<SupplyVisibilityStateModel>,
    { variant }: SetMaterialManagementViews,
  ): Subscription {
    return this.supplyVisibilityService
      .materialManagementViews(variant)
      .subscribe((materialManagementViews: MaterialManagementViews[]): void => {
        const filteredViews = materialManagementViews.filter(
          (q: MaterialManagementViews): boolean => q.enabled === true,
        );
        patchState({
          materialManagementViews: filteredViews,
        });
      });
  }

  /**
   * Set Forecast Active MM View
   * @param {StateContext<SupplyVisibilityStateModel>} { patchState }
   * @param {SetActiveMMView} { mmView}
   * @memberof SupplyVisibilityState
   */
  @Action(SetActiveMMView)
  setActiveMMView(
    { getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { mmView }: SetActiveMMView,
  ): void {
    const state = getState();
    const parameters: BasicParameters = state.basicParameters;

    patchState({
      basicParameters: {
        mmViewID: mmView,
        partNumber: parameters.partNumber,
        plant: parameters.plant,
        vendorCode: parameters.vendorCode,
        weeks: parameters.weeks,
      },
    });
  }

  @Action(SetUserAction)
  setUserAction(
    { patchState }: StateContext<SupplyVisibilityStateModel>,
    { action }: SetUserAction,
  ): void {
    patchState({
      userAction: action,
    });
  }
  /**
   * Reset Buffer Rules
   * @memberof BufferRulesState
   */
  @Action(ResetSupplyVisibilityAndCommits)
  resetSupplyVisibilityAndCommits({
    getState,
    setState,
  }: StateContext<SupplyVisibilityStateModel>): void {
    const pnList = getState().partNumberListParameters;
    const cachedCommits = getState().cachedCommits;
    const cachedForecast = getState().cachedForecast;
    const childrenPNList = getState().childrenPNList;
    const childrenPNInfo = getState().childrenPNInfo;
    const cachedChildrenPNInfo = getState().cachedChildrenPNInfo;
    // const cachedChildrenForecast = getState().cachedChildrenForecast;
    const cachedCommitsHash = getState().cachedCommitsHash;
    const cachedForecastHash = getState().cachedForecastHash;
    const notifications = getState().notifications;
    const notificationsUnreadCount = getState().notificationsUnreadCount;
    const DefaultDashboardData = getState().DefaultDashboardData;
    setState({
      additionalFilters: [],
      basicParameters: {
        mmViewID: '',
        partNumber: '',
        plant: '',
        records: 0,
        variant: '',
        vendorCode: '',
      },
      buyersPartNumbers: [],
      cachedChildrenPNInfo: cachedChildrenPNInfo,
      cachedCommits: cachedCommits,
      cachedCommitsHash: cachedCommitsHash,
      cachedForecast: cachedForecast,
      cachedForecastHash: cachedForecastHash,
      childrenPNInfo: childrenPNInfo,
      // cachedChildrenForecast: cachedChildrenForecast,
      childrenPNList: childrenPNList,
      commitMandatoryDates: [],
      commits: [],
      commitStatus: '',
      commitTransactionDetails: [],
      commitVendorCode: '',
      daterangeParameters: {},
      DefaultDashboardData: DefaultDashboardData,
      dummyCommitsHeaders: [],
      forecast: {
        days: undefined,
        materialManagementViewID: '',
        mergeProjections: [],
        partNumber: '',
        plant: '',
        projectionDetails: [],
        reviewed: undefined,
        staticvalues: [],
        variant: '',
        vendorCode: '',
        weeks: undefined,
      },
      forecastWeekRange: {
        endWeek: 0,
        startWeek: 0,
      },
      materialManagementViews: [],
      newCommit: null,
      newUserSettings: { data: '', key: '' },
      notificationDetailCache: {},
      notifications: notifications,
      notificationsUnreadCount: notificationsUnreadCount,
      partNumberListParameters: pnList,
      partnumberTransactionDetails: [],
      purchaseOrders: [],
      selectedDummyCommitHeader: { name: '', partNumber: '' },
      svComments: [],
      svNotes: [],
      svNotesCount: 0,
      svPredefinedComments: [],
      userAction: EnumSvSidebarSection.None,
      userSettingParameters: { key: '' },
      userSettings: { data: '', key: '' },
      vendorCodes: [],
      vendorList: '',
      vendorTransactionDetails: [],
    });
  }

  @Action(ResetSupplyVisibilityPartNumberList)
  resetSupplyVisibilityPartNumberList({
    patchState,
  }: StateContext<SupplyVisibilityStateModel>): void {
    // const pnList = getState();
    patchState({
      partNumberListParameters: {},
    });
  }

  @Action(ResetCache)
  resetCache({ patchState }: StateContext<SupplyVisibilityStateModel>): void {
    //Resetting childrenForecasts Data
    this.forecastsData = {};
    this.virtualPnChildList = {};

    patchState({
      cachedCommits: undefined,
      cachedCommitsHash: undefined,
      cachedForecast: undefined,
      cachedForecastHash: undefined,
      childrenPNInfo: undefined,
      childrenPNList: undefined,
    });
  }

  @Action(ResetCommitTransactionDetails)
  resetCommitTransactionDetails({ patchState }: StateContext<SupplyVisibilityStateModel>): void {
    patchState({ commitTransactionDetails: [] });
  }

  /**
   * Reset plant dependent cache fields
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, patchState }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(ResetPlantDependentCache)
  async resetPlantDependentCache({
    patchState,
  }: StateContext<SupplyVisibilityStateModel>): Promise<void> {
    //Fields will be reset when plant changes (action dispatched in auth state)
    patchState({
      cachedCommitsCarriers: undefined,
      cachedCommitsCountries: undefined,
      cachedCommitsReasons: undefined,
      cachedCommitsTransportType: undefined,
      cachedReadOnlyFields: undefined,
      commitMandatoryDates: undefined,
    });
  }

  /**
   * Cache Reasons from API
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, patchState }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(CacheCommitsReasons)
  async cacheCommitsReasons(
    { dispatch, getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { refresh }: CacheCommitsReasons,
  ): Promise<Subscription | void> {
    let state = getState();
    if (refresh || !state.cachedCommitsReasons) {
      return this.commitsService.getReasonsList().subscribe(
        (response: Reason[]): void => {
          if (response) {
            patchState({ cachedCommitsReasons: [...response] });
          }
          dispatch(new CacheCommitsReasonsSuccess());
        },
        (error: unknown): void => {
          dispatch(new CacheCommitsReasonsError(error));
        },
      );
    } else {
      dispatch(new CacheCommitsReasonsSuccess());
    }
  }

  /**
   * Cache Carriers from API (Forwarder)
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, patchState }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(CacheCommitsCarriers)
  async cacheCommitsCarriers(
    { dispatch, getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { refresh }: CacheCommitsCarriers,
  ): Promise<Subscription | void> {
    let state = getState();
    if (refresh || !state.cachedCommitsCarriers) {
      return this.commitsService.getCommitsCarriers().subscribe(
        (response: ApiResponse<Carriers[]>): void => {
          if (response) {
            response.result = Helper.setOrderByAlpha('name', response.result);
            patchState({
              cachedCommitsCarriers: response.result,
            });
          }
          dispatch(new CacheCommitsCarriersSuccess());
        },
        (error: unknown): void => {
          dispatch(new CacheCommitsCarriersError(error));
        },
      );
    } else {
      dispatch(new CacheCommitsCarriersSuccess());
    }
  }

  /**
   * Cache Vendors Name from API GetVendorsWithNames
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, patchState}
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(CacheVendorsName)
  async cacheVendorsName(
    { dispatch }: StateContext<SupplyVisibilityStateModel>,
    { onlyForBuy }: CacheVendorsName,
  ): Promise<Subscription | void> {
    return this.commitsService.getVendorsWithNames(onlyForBuy).subscribe(
      (response: ApiResponse<VendorName[]> | VendorName[]): void => {
        const result = Array.isArray(response) ? response : response?.result;
        dispatch(new CacheVendorsNameSuccess(result ?? []));
      },
      (error: unknown): void => {
        dispatch(new CacheVendorsNameError(error));
      },
    );
  }

  /**
   * Cache Countries from API
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, patchState }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(CacheCommitsCountries)
  async cacheCommitsCountries(
    { dispatch, getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { refresh }: CacheCommitsCountries,
  ): Promise<Subscription | void> {
    let state = getState();
    if (refresh || !(state.cachedCommitsCountries && state.cachedCommitsCountries.length)) {
      return this.commitsService.getCommitsCountries().subscribe(
        (response: ApiResponse<Countries[]>): void => {
          if (response) {
            patchState({ cachedCommitsCountries: response.result });
          }
          dispatch(new CacheCommitsCountriesSuccess());
        },
        (error): void => {
          dispatch(new CacheCommitsCountriesError(error));
        },
      );
    } else {
      dispatch(new CacheCommitsCountriesSuccess());
    }
  }

  /**
   * Cache Transport Type from API
   * @param {StateContext<SupplyVisibilityStateModel>} { getState, patchState }
   * @returns
   * @memberof SupplyVisibilityState
   */
  @Action(CacheCommitsTransportType)
  async cacheCommitsTransportType(
    { dispatch, getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { refresh }: CacheCommitsTransportType,
  ): Promise<Subscription | void> {
    let state = getState();
    if (refresh || !state.cachedCommitsTransportType) {
      return this.commitsService.getCommitsTransportType().subscribe(
        (response): void => {
          if (response) {
            patchState({ cachedCommitsTransportType: response });
          }
          dispatch(new CacheCommitsTransportTypeSuccess());
        },
        (error): void => {
          dispatch(new CacheCommitsTransportTypeError(error));
        },
      );
    } else {
      dispatch(new CacheCommitsTransportTypeSuccess());
    }
  }

  @Action(CacheReadOnlyFields)
  async cacheReadOnlyFields(
    { dispatch, getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { refresh }: CacheReadOnlyFields,
  ): Promise<Subscription | void> {
    let state = getState();
    if (refresh || !(state.cachedReadOnlyFields && state.cachedReadOnlyFields.length)) {
      return this.supplyVisibilityService.getReadOnlyFieldsForCommits().subscribe(
        (response): void => {
          if (response) {
            patchState({ cachedReadOnlyFields: [...response] });
          } else {
            patchState({ cachedReadOnlyFields: [] });
          }
          dispatch(new CacheReadOnlyFieldsSuccess(response));
        },
        (error): void => {
          dispatch(new CacheReadOnlyFieldsError(error));
        },
      );
    } else {
      dispatch(new CacheReadOnlyFieldsSuccess(state.cachedReadOnlyFields));
    }
  }

  @Action(GetFilterAdditions)
  getFilterAdditions({
    dispatch,
    getState,
    patchState,
  }: StateContext<SupplyVisibilityStateModel>): Subscription | void {
    let state = getState();
    if (!state.commitFilterAdditions) {
      return this.supplyVisibilityService.getFilterAdditionsForCommits().subscribe(
        (response): void => {
          response = response && response.length ? response : [];
          patchState({ commitFilterAdditions: [...response] });
          dispatch(new GetFilterAdditionsSuccess(response));
        },
        (error): void => {
          dispatch(new GetFilterAdditionsError(error));
        },
      );
    } else {
      dispatch(new GetFilterAdditionsSuccess(state.commitFilterAdditions));
    }
  }

  @Action(SetCommitTransactionDetails)
  setCommitTransactionDetails(
    { getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { commitTransactionDetails }: SetCommitTransactionDetails,
  ): void {
    const state = getState();

    //For adding newly created commit in commitTransaction
    if (commitTransactionDetails.type == EnumCommitType.NEW) {
      let commits = state.commitTransactionDetails.filter(
        (commit: CommitTransactionDetails): boolean => commit.type != EnumCommitType.NEW,
      );
      commits.push(commitTransactionDetails);
      patchState({ commitTransactionDetails: commits });
    }

    //For Updating edited,merged or splitted commit data in commitTransaction
    if (
      commitTransactionDetails.type == EnumCommitType.EDIT ||
      commitTransactionDetails.type == EnumCommitType.SPLIT ||
      commitTransactionDetails.type == EnumCommitType.MERGE
    ) {
      let updatedCommitIndex = state.commitTransactionDetails.findIndex(
        (commit: CommitTransactionDetails): boolean => commit.id == commitTransactionDetails.id,
      );
      if (updatedCommitIndex != -1) {
        patchState({
          commitTransactionDetails: [
            ...state.commitTransactionDetails.filter(
              (commit: CommitTransactionDetails): boolean => {
                return (
                  commit.id != commitTransactionDetails.id &&
                  commit.type != commitTransactionDetails.type
                );
              },
            ),
            commitTransactionDetails,
          ],
        });
      } else {
        let index = state.commitTransactionDetails.findIndex(
          (commit: CommitTransactionDetails): boolean =>
            commit.type == commitTransactionDetails.type,
        );
        if (index != -1) {
          patchState({
            commitTransactionDetails: [
              ...state.commitTransactionDetails.filter(
                (commit): boolean => commit.type != commitTransactionDetails.type,
              ),
              commitTransactionDetails,
            ],
          });
        } else {
          let modifiedCommitTransactions = Helper.createCopy(state.commitTransactionDetails);
          modifiedCommitTransactions.push(commitTransactionDetails);
          patchState({ commitTransactionDetails: modifiedCommitTransactions });
        }
      }
    }
  }

  @Action(PatchCommitTransactionDetails)
  async patchCommitTransactionDetails({
    getState,
    patchState,
  }: StateContext<SupplyVisibilityStateModel>): Promise<void> {
    const state = getState();
    //Checking if commitTransactions are reset (undefined or null)
    if ((state.commitTransactionDetails || []).length > 0) {
      patchState({ commitTransactionDetails: [] });
    }
  }

  @Action(SetVendorTransactionDetails)
  setVendorTransactionDetails(
    { getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { vendorTransactionDetails }: SetVendorTransactionDetails,
  ): void {
    const state = getState();

    //For Updating edited vendor data in vendorTransaction
    if (vendorTransactionDetails.type == EnumVendorType.EDIT) {
      let updatedVendorIndex = state.vendorTransactionDetails.findIndex(
        (vendor): boolean => vendor.id == vendorTransactionDetails.id,
      );
      if (updatedVendorIndex != -1) {
        patchState({
          vendorTransactionDetails: [
            ...state.vendorTransactionDetails.filter((vendor): boolean => {
              return (
                vendor.id != vendorTransactionDetails.id &&
                vendor.type != vendorTransactionDetails.type
              );
            }),
            vendorTransactionDetails,
          ],
        });
      } else {
        const index = (state.vendorTransactionDetails || []).findIndex(
          (vendor: VendorTransactionDetails): boolean =>
            vendor.vendorCode === vendorTransactionDetails.vendorCode,
        );
        if (index === -1) {
          patchState({
            vendorTransactionDetails: [
              ...state.vendorTransactionDetails.filter(
                (vendor): boolean => vendor.type != vendorTransactionDetails.type,
              ),
              vendorTransactionDetails,
            ],
          });
        } else {
          let modifiedVendorTransactions = Helper.createCopy(state.vendorTransactionDetails);
          modifiedVendorTransactions.push(vendorTransactionDetails);
          patchState({ vendorTransactionDetails: modifiedVendorTransactions });
        }
      }
    }
  }

  @Action(PatchVendorTransactionDetails)
  async patchVendorTransactionDetails(
    { getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { vendorTransactionDetails }: PatchVendorTransactionDetails,
  ): Promise<void> {
    const state = getState();
    //Checking if vendorTransactions are reset (undefined or null)
    if (state.vendorTransactionDetails) {
      patchState({ vendorTransactionDetails: vendorTransactionDetails });
    }
  }

  // @Action(SetQuotationTransactionDetails)
  // setQuotationTransactionDetails(
  //   { getState, patchState }: StateContext<SupplyVisibilityStateModel>,
  //   { quotationTransactionDetails }: SetQuotationTransactionDetails,
  // ): void {
  //   const state = getState();

  //   if (quotationTransactionDetails.type == EnumQuotationType.EDIT) {
  //     let updatedQuotationIndex = state.quotationTransactionDetails.findIndex(
  //       (quotation): boolean => quotation.id == quotationTransactionDetails.id,
  //     );
  //     if (updatedQuotationIndex != -1) {
  //       patchState({
  //         quotationTransactionDetails: [
  //           ...state.quotationTransactionDetails.filter((quotation): boolean => {
  //             return quotation.id != quotationTransactionDetails?.id && Boolean(quotation.type);
  //           }),
  //           quotationTransactionDetails,
  //         ],
  //       });
  //     } else {
  //       let index = state.quotationTransactionDetails.findIndex(
  //         (commit): boolean => commit.type == quotationTransactionDetails.type,
  //       );
  //       if (index != -1) {
  //         if ((state.commits || []).length > 0) {
  //           patchState({ commits: [] });
  //         }
  //         if ((state.commitTransactionDetails || []).length > 0) {
  //           patchState({ commitTransactionDetails: [] });
  //         }
  //         if ((state.quotationTransactionDetails || []).length > 0) {
  //           patchState({ quotationTransactionDetails: [] });
  //         }
  //         patchState({
  //           quotationTransactionDetails: [
  //             ...state.quotationTransactionDetails.filter(
  //               (quotation: QuotationTransactionDetails): boolean =>
  //                 quotation.type != quotationTransactionDetails.type,
  //             ),
  //             quotationTransactionDetails,
  //           ],
  //         });
  //       } else {
  //         let modifiedQuotationTransactions = Helper.createCopy(state.quotationTransactionDetails);
  //         modifiedQuotationTransactions.push(quotationTransactionDetails);
  //         patchState({ quotationTransactionDetails: modifiedQuotationTransactions });
  //       }
  //     }
  //   }
  // }

  // @Action(PatchQuotationTransactionDetails)
  // async patchQuotationTransactionDetails(
  //   { getState, patchState }: StateContext<SupplyVisibilityStateModel>,
  //   { quotationTransactionDetails }: PatchQuotationTransactionDetails,
  // ): Promise<void> {
  //   const state = getState();
  //   if (state.quotationTransactionDetails) {
  //     patchState({ quotationTransactionDetails: quotationTransactionDetails });
  //   }
  // }

  // TODO: Remove this commented action when notifications are implemented
  @Action(ShowNotifications)
  showNotifications(
    ctx: StateContext<SupplyVisibilityStateModel>,
    { id }: ShowNotifications,
  ): void {
    import('../../pages/notifications/notifications.component').then(
      ({ NotificationsComponent }): void => {
        if (id) {
          this.dialog.open(NotificationsComponent, {
            // Material 21 defaults `autoFocus` to `'first-tabbable'`, which
            // would land focus on the "Mark all as read" button in the
            // dialog header — visually misreads as if the user already
            // clicked it. Focus the dialog container itself instead.
            autoFocus: 'dialog',
            data: {
              id: id,
            },
            height: '80%',
            width: '70%',
          });
        }
      },
    );
  }

  @Action(SetPlantMandatoryDates)
  async setPlantMandatoryDates(
    { dispatch, getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { refresh }: SetPlantMandatoryDates,
  ): Promise<void> {
    const state = getState();

    if (refresh || !state.commitMandatoryDates?.length) {
      let dates: string[];

      try {
        dates = await firstValueFrom(this.commitsService.getMandatoryDates());
      } catch (error) {
        dates = ['ETADate'];
        console.warn(`${state.commitMandatoryDates?.[0]} is Plant Specific Lead Date`);
        dispatch(new SetPlantMandatoryDatesError(error));
      }

      patchState({
        commitMandatoryDates: dates,
      });
    }

    dispatch(new SetPlantMandatoryDatesSuccess());
  }

  initNotifications(): void {
    this.authService
      .getIsAuthorized()
      .pipe(
        filter((result): boolean => !!result?.isAuthenticated),
        take(1),
      )
      .subscribe((): void => {
        this.store.dispatch(new GetNotifications());
        this.store.dispatch(new GetNotificationsUnreadCount());
      });
  }

  //
  public modifyCommitTransaction(
    commits: Commit[],
    state: SupplyVisibilityStateModel,
  ): CommitTransactionDetails[] {
    let commitTransactionDetails = Helper.createCopy(state.commitTransactionDetails);

    if (commitTransactionDetails && commitTransactionDetails.length) {
      let updatedDetails = this.addCommitTransactions(commits, commitTransactionDetails);
      commits = updatedDetails.response;
      commitTransactionDetails = updatedDetails.commitTransactionDetails;
      return this.updateCommitTransaction(commits, commitTransactionDetails, state);
    }
    return []; // Added return for cases where commitTransactionDetails is empty
  }

  //For pushing newly created commits into the API response
  addCommitTransactions(
    response: Commit[],
    commitTransactionDetails: CommitTransactionDetails[],
  ): { commitTransactionDetails: CommitTransactionDetails[]; response: Commit[] } {
    let commitTransactions = commitTransactionDetails.filter(
      (commit: CommitTransactionDetails): boolean | undefined => {
        return (
          commit.type == EnumCommitType.NEW ||
          (commit.type == EnumCommitType.EDIT && commit.isCache) ||
          (commit.type == EnumCommitType.SPLIT && commit.isCache) ||
          (commit.type == EnumCommitType.MERGE && commit.isCache)
        );
      },
    );
    let newCommitTransactions: CommitTransactionDetails[] = commitTransactions.slice(-1);

    for (let commitIndex = 0; commitIndex < newCommitTransactions.length; commitIndex++) {
      let highLightCommits: Commit[] = [];
      // if (response.length) {
      if (
        newCommitTransactions[commitIndex].type == EnumCommitType.NEW ||
        newCommitTransactions[commitIndex].type == EnumCommitType.EDIT
      ) {
        //Checking if its present in API response
        const data = newCommitTransactions[commitIndex]?.data as Commit;
        let index = response.findIndex(
          (commit): boolean => commit.inboundDeliveryNumber == data.inboundDeliveryNumber,
        );
        if (index != -1) {
          //Updating index of the commit already present
          let oldCommitIndex = commitTransactionDetails.findIndex(
            (commit): boolean => commit.id == response[index].inboundDeliveryNumber,
          );
          let updatedCommit = commitTransactionDetails[oldCommitIndex];
          updatedCommit.datagridInfo = { index: index };
          commitTransactionDetails.splice(commitIndex, 1);
          commitTransactionDetails.push(updatedCommit);
        } else {
          highLightCommits.push(newCommitTransactions[commitIndex]?.data as Commit);
        }
      }

      if (newCommitTransactions[commitIndex].type == EnumCommitType.SPLIT) {
        let index = response.findIndex(
          (commit): boolean =>
            commit.inboundDeliveryNumber == newCommitTransactions[commitIndex].id,
        );
        if (index != -1) {
          // updating split commit in commit transaction
          let oldCommitIndex = commitTransactionDetails.findIndex(
            (commit): boolean => commit.id == response[index].inboundDeliveryNumber,
          );
          let updatedCommit = commitTransactionDetails[oldCommitIndex];
          updatedCommit.datagridInfo = { index: index };
          commitTransactionDetails.splice(commitIndex, 1);
          commitTransactionDetails.push(updatedCommit);
        }
        // checking if children commits are present in API response
        for (
          let datagridCommitIndex = 0;
          datagridCommitIndex < (newCommitTransactions[commitIndex].data as Commit[])?.length;
          datagridCommitIndex++
        ) {
          const data = newCommitTransactions[commitIndex].data as Commit[];
          let splitIndex = response.findIndex(
            (commit): boolean =>
              commit.inboundDeliveryNumber == data[datagridCommitIndex]?.inboundDeliveryNumber,
          );
          if (splitIndex == -1) {
            highLightCommits.push(data[datagridCommitIndex]);
          }
        }
      }

      if (newCommitTransactions[commitIndex].type == EnumCommitType.MERGE) {
        const data = newCommitTransactions[commitIndex].data as Commit[];
        let index = response.findIndex(
          (commit): boolean => commit.inboundDeliveryNumber == data[0].inboundDeliveryNumber,
        );
        if (index != -1) {
          //Updating index of the commit already present
          let oldCommitIndex = commitTransactionDetails.findIndex(
            (commit): boolean => commit.id == response[index].inboundDeliveryNumber,
          );
          let updatedCommit = commitTransactionDetails[oldCommitIndex];
          updatedCommit.datagridInfo = { index: index };
          commitTransactionDetails.splice(commitIndex, 1);
          commitTransactionDetails.push(updatedCommit);
        } else {
          highLightCommits.push(data[0]);
        }
      }

      if (highLightCommits) {
        for (let i = 0; i < highLightCommits.length; i++) {
          response.unshift(highLightCommits[i]);
        }
      }
      // }
    }
    let updatedDetails = { commitTransactionDetails: commitTransactionDetails, response: response };
    return updatedDetails;
  }

  // For setting index of already present commit in API response
  updateCommitTransaction(
    response: Commit[],
    commitTransactionDetails: CommitTransactionDetails[],
    state: SupplyVisibilityStateModel,
  ): CommitTransactionDetails[] {
    let result = commitTransactionDetails;
    let editIndex = commitTransactionDetails.findIndex(
      (commit): boolean => commit.type == EnumCommitType.EDIT,
    );
    let splitIndex = commitTransactionDetails.findIndex(
      (commit): boolean => commit.type == EnumCommitType.SPLIT,
    );
    let mergeIndex = commitTransactionDetails.findIndex(
      (commit): boolean => commit.type == EnumCommitType.MERGE,
    );

    if (editIndex != -1 || splitIndex != -1 || mergeIndex != -1) {
      if (editIndex != -1) {
        result = this.setCommitIndex(commitTransactionDetails, editIndex, response, state);
      } else if (splitIndex != -1) {
        result = this.setCommitIndex(result, splitIndex, response, state);
      } else if (mergeIndex != -1) {
        result = this.setCommitIndex(result, mergeIndex, response, state);
      }
    }
    return result;
  }

  setCommitIndex(
    commitTransactionDetails: CommitTransactionDetails[],
    index: number,
    response: Commit[],
    state: SupplyVisibilityStateModel,
  ): CommitTransactionDetails[] {
    if (
      commitTransactionDetails[index].datagridInfo &&
      typeof commitTransactionDetails[index].datagridInfo.index != 'number'
    ) {
      let commitIndex = response.findIndex(
        (commit): boolean =>
          commit.inboundDeliveryNumber == state.commitTransactionDetails[index].id,
      );
      if (commitIndex != -1) {
        let updatedCommit = commitTransactionDetails[index];
        updatedCommit.datagridInfo = { index: commitIndex };
        commitTransactionDetails.splice(index, 1);
        commitTransactionDetails.push(updatedCommit);
      }
    }
    return commitTransactionDetails;
  }

  //Get Children forecast from state if available, otherwise from API
  async setChildrenForecast(
    state: SupplyVisibilityStateModel,
    parameters: BasicParameters,
  ): Promise<{ data: ForecastVirtualVc[] }> {
    if (this.forecastsData[parameters.partNumber] && SupplyVisibilityState.isForecastDataChanged) {
      return this.forecastsData[parameters.partNumber] as { data: ForecastVirtualVc[] };
    } else {
      let data = {
        dummyCommitHeaders: state.dummyCommitsHeaders,
        mmViewID: parameters.mmViewID,
        partNumber: parameters.partNumber,
        plant: parameters.plant,
        records: parameters.records,
        variant: parameters.variant,
        vendorCode: parameters.vendorCode,
        widgetId: parameters.widgetId,
      };
      console.log('CALLING CHILDREN PN API!! --------------');
      console.log(state.childrenPNInfo);
      const virtualVcChildList: Forecast[] = state.forecast.mergeProjections as Forecast[];
      let virtualVcForecasts: ForecastVirtualVc[] =
        await this.supplyVisibilityService.getChildrenPnForecast(data, virtualVcChildList);
      if (virtualVcForecasts && virtualVcForecasts.length) {
        this.forecastsData[parameters.partNumber] = { data: virtualVcForecasts };
      } else {
      }
    }
    SupplyVisibilityState.isForecastDataChanged = true;
    return this.forecastsData[parameters.partNumber] as { data: ForecastVirtualVc[] };
  }

  isVirtualVC(state: SupplyVisibilityStateModel): boolean {
    return state.basicParameters.vendorCode == 'VirtualVC';
  }

  isValidPn(state: SupplyVisibilityStateModel, partNumber: string, vendorCode: string): boolean {
    if (state && state.basicParameters) {
      return (
        state.basicParameters.partNumber == partNumber ||
        state.basicParameters.vendorCode == vendorCode
      );
    }
    return false;
  }

  @Action(DownloadNotificationFile)
  async downloadNotificationFile(
    ctx: StateContext<SupplyVisibilityStateModel>,
    { url }: DownloadNotificationFile,
  ): Promise<void> {
    const fileName = decodeURIComponent(url.split('/').pop() ?? '') || 'download';

    try {
      const blob = await firstValueFrom(this.supplyVisibilityService.downloadFileByUrl(url));
      FileSaver.saveAs(blob, fileName);
    } catch {
      this.notification.showError(
        this.translate.instant('supply-visibility.downloadNotificationFileError'),
      );
    }
  }

  @Action(HandleLinkEvents)
  async handleLinkEvents(
    ctx: StateContext<SupplyVisibilityStateModel>,
    { event }: HandleLinkEvents,
  ): Promise<string | void> {
    if (!Helper.getLinkEventRegex(true).test(event)) {
      return;
    }

    //Process Event Data
    let eventName: string | undefined, eventValue: string | undefined;
    const nameMatch = event.match(/-[a-z]+:/gm);
    const valueMatch = event.match(/:[0-9]+/gm);
    eventName = nameMatch ? nameMatch[0] : undefined;
    eventName = eventName ? eventName.substring(1, eventName.length - 1) : undefined;
    eventValue = valueMatch ? valueMatch[0] : undefined;
    eventValue = eventValue ? eventValue.substring(1, eventValue.length) : undefined;

    //Events
    switch (eventName) {
      case 'commit':
        if (eventValue) {
          return this.onCommitEditEvent(eventName, eventValue);
        }
        break;
      case 'deleteCommit':
        //To be Implemented
        break;
      /*ADD Other Events*/
      default:
        //Unknown Event
        break;
    }
  }

  /**
   * Show Loading Screen While Link event process in background
   * @param eventName
   * @returns dialog ref for customization
   */
  // openLinkEventDialog(eventName: string): MatDialogRef<LinkEventsDialogComponent> {
  //   const dialogRef = this.dialog.open(LinkEventsDialogComponent, {
  //     data: {
  //       content: this.translate.instant(`supply-visibility.linkEvent-${eventName}`),
  //       hideHeader: true,
  //     },
  //     disableClose: true,
  //     // height: '100px',
  //     width: '400px',
  //   });
  //   return dialogRef;
  // }

  private async onCommitEditEvent(eventName: string, eventValue: string): Promise<string | void> {
    let commitData: Commit[] = [];
    try {
      commitData =
        (await firstValueFrom(
          this.commitsService.getCommits([{ key: 'inboundDeliveryNumber', value: eventValue }]),
        )) ?? [];
    } catch {
      commitData = [];
    }
    if (commitData && commitData.length) {
      const commit = commitData.find((e): boolean => e.inboundDeliveryNumber == eventValue);
      if (commit) {
        let commitWithModifiedDates = this.dateService.commitHistoryTableDates({ ...commit });
        this.store.dispatch(
          new TriggerLinkEventCommitDialog({
            eventName: eventName,
            eventValue: commitWithModifiedDates,
          }),
        );
        this.store.dispatch(new HandleLinkEventsSuccess());
      } else {
        this.onCommitEditError(eventName, eventValue);
      }
    } else {
      this.onCommitEditError(eventName, eventValue);
    }
  }
  private onCommitEditError(eventName: string, eventValue: string): void {
    this.store.dispatch(new HandleLinkEventsError());
    const errorMsg = `${this.translate.instant(
      `supply-visibility.linkEvent-${eventName}-error`,
    )} ${eventValue}!`;
    this.notification.showError(errorMsg);
    console.log(errorMsg);
  }
  /* Add Other Link Event Functionality*/

  /**
   * @param parameters
   * @returns childrenList from cache if available, otherwise from API
   */
  // async getChildrenPNList(parameters) {
  //   if (this.virtualPnChildList[parameters.partNumber]) { return this.virtualPnChildList[parameters.partNumber] }
  //   else {
  //     let childrenPNList = await this.supplyVisibilityService.getVirtualPnChildList(parameters.partNumber)
  //     this.virtualPnChildList[parameters.partNumber] = childrenPNList;
  //     return childrenPNList;
  //   }
  // }
  @Action(SetDefaultDashboardSettings)
  setDefaultDashboard(
    { getState, patchState }: StateContext<SupplyVisibilityStateModel>,
    { data, key }: SetDefaultDashboardSettings,
  ): void {
    const state = getState();
    if (key == 'defaultDashboardId') {
      const dashboardData = data as { id: string; plant: string };
      patchState({
        DefaultDashboardData: {
          defaultDashboardId: {
            [dashboardData.plant]: dashboardData.id,
          },
          isLogin: state.DefaultDashboardData.isLogin,
          isPlantChanged: state.DefaultDashboardData.isPlantChanged,
        },
      });
    }
    if (key == 'isPlantChanged') {
      const plantChanged = data as boolean;
      patchState({
        DefaultDashboardData: {
          defaultDashboardId: state.DefaultDashboardData.defaultDashboardId,
          isLogin: state.DefaultDashboardData.isLogin,
          isPlantChanged: plantChanged,
        },
      });
    }
    if (key == 'isLogin') {
      const login = data as boolean;
      patchState({
        DefaultDashboardData: {
          defaultDashboardId: state.DefaultDashboardData.defaultDashboardId,
          isLogin: login,
          isPlantChanged: state.DefaultDashboardData.isPlantChanged,
        },
      });
    }
  }

  @Action(SetCommitStatus)
  setCommitStatus(
    { patchState }: StateContext<SupplyVisibilityStateModel>,
    { status }: SetCommitStatus,
  ): void {
    patchState({
      commitStatus: status,
    });
  }

  @Action(SetAdditionalFilters)
  setAdditionalFilters(
    { patchState }: StateContext<SupplyVisibilityStateModel>,
    { filters }: SetAdditionalFilters,
  ): void {
    patchState({
      additionalFilters: filters,
    });
  }

  @Action(GetMultiProjections)
  getMultiProjections(
    { dispatch }: StateContext<SupplyVisibilityStateModel>,
    { payload }: GetMultiProjections,
  ): Observable<void> {
    return this.supplyVisibilityService.getHeaderForPrecalculate().pipe(
      map((header: DummyCommitReturnHeader): CumulativePayload => ({
        ...payload,
        DummyCommitHeaderID: header.id,
      })),
      switchMap((updatedPayload): Observable<Forecast[]> =>
        this.supplyVisibilityService.getMultiProjections(updatedPayload),
      ),
      switchMap((res: Forecast[]): Observable<void> =>
        dispatch(new GetMultiProjectionsSuccess(Array.isArray(res) ? res : [])),
      ),
      catchError((error): Observable<void> => dispatch(new GetMultiProjectionsError(error))),
    );
  }

  @Action(GetTemplateSettings)
  getTemplateSettings(
    { dispatch }: StateContext<SupplyVisibilityStateModel>,
    { params }: GetTemplateSettings,
  ): void {
    this.supplyVisibilityService.getTemplateSettings(params).subscribe({
      error: (error): void => {
        dispatch(new GetTemplateSettingsError(error));
      },
      next: (res): void => {
        if (res) {
          dispatch(new GetTemplateSettingsSuccess(res));
        }
      },
    });
  }

  @Action(GetTemplateSettingsByID)
  getTemplateSettingsByGetTemplateSettingsByID(
    { dispatch }: StateContext<SupplyVisibilityStateModel>,
    { id }: GetTemplateSettingsByID,
  ): void {
    this.supplyVisibilityService.getTemplateSettingsByID(id).subscribe({
      error: (error): void => {
        dispatch(new GetTemplateSettingsByIDError(error));
      },
      next: (res): void => {
        if (res) {
          dispatch(new GetTemplateSettingsByIDSuccess(res));
        }
      },
    });
  }

  @Action(UpdateTemplateSettings)
  updateTemplateSettings(
    { dispatch }: StateContext<SupplyVisibilityStateModel>,
    { payload }: UpdateTemplateSettings,
  ): Subscription {
    return this.supplyVisibilityService.updateTemplateSettings(payload).subscribe({
      error: (error): void => {
        dispatch(new UpdateTemplateSettingsError(error));
      },
      next: (res): void => {
        dispatch(new UpdateTemplateSettingsSuccess(res));
      },
    });
  }

  @Action(CreateTemplateSettings)
  createTemplateSettings(
    { dispatch }: StateContext<SupplyVisibilityStateModel>,
    { data }: CreateTemplateSettings,
  ): void {
    this.supplyVisibilityService.createTemplateSettings(data).subscribe({
      error: (error): void => {
        dispatch(new CreateTemplateSettingsError(error));
      },
      next: (res): void => {
        if (res) {
          dispatch(new CreateTemplateSettingsSuccess(res));
        }
      },
    });
  }

  @Action(DeleteTemplateSettings)
  deleteTemplateSettings(
    { dispatch }: StateContext<SupplyVisibilityStateModel>,
    { id }: DeleteTemplateSettings,
  ): void {
    this.supplyVisibilityService.deleteTemplateSettings(id).subscribe({
      error: (error): void => {
        dispatch(new DeleteTemplateSettingsError(error));
      },
      next: (): void => {
        dispatch(new DeleteTemplateSettingsSuccess());
      },
    });
  }
}
